import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, serverTimestamp, increment, limit as fsLimit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getPerformance } from 'firebase/performance';
import { getStorage } from 'firebase/storage';
import { format } from 'date-fns';

// No forzar redirecciones de dominio para no romper el modo PWA (standalone)
// La app puede ejecutarse en reserberry.web.app o www.reserberry.com; evitar cambios de origen aquí.
try {
  // Si no estamos en standalone, igualmente no forzamos redirección para preservar instalación PWA.
} catch {}


// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyCD9Ti-4YAxWCLliIrc5HYXgsIerJ8p9Ec",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "gestion-integral-unas.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "gestion-integral-unas",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "gestion-integral-unas.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "1009673082117",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:1009673082117:web:535c63770ca81fd931536c",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "G-MMHBTENPKE"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, 'europe-west1');
// Performance Monitoring (web)
let perf = null;
try { perf = getPerformance(app); } catch {}

// ===== Analytics (web) — se inicializa solo tras consentimiento explícito =====
let analytics = null;
const hasAnalyticsConsent = () => {
  try {
    return (localStorage.getItem('consent.analytics') === 'granted');
  } catch { return false; }
};

export const initAnalyticsIfConsented = () => {
  try {
    if (typeof window === 'undefined') return;
    if (analytics) return; // ya inicializado
    if (!hasAnalyticsConsent()) return;
    isSupported()
      .then((supported) => {
        if (!supported) return;
        try {
          analytics = getAnalytics(app);
          try {
            try {
              const sp = new URLSearchParams(window.location.search || '');
              if (sp.get('debug_mode') === '1') {
                try { localStorage.setItem('ga_debug', '1'); } catch {}
              }
            } catch {}
            logEvent(analytics, 'page_view', { page_path: window.location.pathname });
          } catch {}
        } catch {}
      })
      .catch(() => {});
  } catch {}
};

// Intento inicial al cargar (solo si ya existe consentimiento previo)
try { initAnalyticsIfConsented(); } catch {}

// Exportar instancias para uso en otros archivos
export { auth, db, storage };
export { functions };
export { analytics };
export { perf };

// Utilidades para registrar eventos con contexto básico
const getEventContext = () => {
  try {
    const ctx = {};
    if (typeof window !== 'undefined') {
      ctx.page_path = window.location?.pathname;
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      ctx.screen_w = w; ctx.screen_h = h;
      ctx.device = w < 640 ? 'mobile' : (w < 1024 ? 'tablet' : 'desktop');
      const p = ctx.page_path || '';
      ctx.channel = /\/admin/i.test(p) ? 'admin' : (/\/booking/i.test(p) ? 'client' : 'public');
      try {
        const search = window.location?.search || '';
        const sp = new URLSearchParams(search);
        if (sp.get('debug_mode') === '1') {
          // Hacer visibles los eventos en DebugView inmediatamente
          ctx.debug_mode = true;
        }
        // Si se activó debug previamente, persistirlo durante la sesión
        try { if (!ctx.debug_mode && localStorage.getItem('ga_debug') === '1') ctx.debug_mode = true; } catch {}
        // Idioma de la app (para segmentar informes)
        try {
          const savedLang = localStorage.getItem('app_lang');
          if (savedLang) ctx.lang = savedLang;
        } catch {}
      } catch {}
      if (__cachedTenantId) ctx.tenantId = __cachedTenantId;
    }
    return ctx;
  } catch { return {}; }
};

export const trackEvent = (name, params = {}) => {
  try {
    if (analytics) {
      const base = getEventContext();
      logEvent(analytics, name, { ...base, ...(params || {}) });
    }
  } catch {}
};

export const trackPageView = (path) => {
  try {
    const page_path = path || (typeof window !== 'undefined' ? window.location.pathname : undefined);
    trackEvent('page_view', { page_path });
  } catch {}
};

// Reportar excepciones a GA4
export const trackException = (errorOrMessage, params = {}) => {
  try {
    const description =
      typeof errorOrMessage === 'string'
        ? errorOrMessage
        : (errorOrMessage?.message || 'Unknown error');
    trackEvent('exception', {
      description,
      fatal: !!params.fatal,
      ...params,
    });
  } catch {}
};

// ===== Helper de Tenant activo (claims) =====
let __cachedTenantId = null;
onAuthStateChanged(auth, async (user) => {
  __cachedTenantId = null;
  if (!user) return;
  try {
    const token = await user.getIdTokenResult();
    __cachedTenantId = token?.claims?.tenantId || null;
  } catch {
    __cachedTenantId = null;
  }
  // Refresh ID token periodically to avoid stale sessions
  try {
    if (typeof window !== 'undefined') {
      if (window.__rb_tokenRefreshInterval) {
        clearInterval(window.__rb_tokenRefreshInterval);
      }
      window.__rb_tokenRefreshInterval = setInterval(async () => {
        try { await user.getIdToken(true); } catch {}
      }, 5 * 60 * 1000);
    }
  } catch {}
});

export const getActiveTenantId = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdTokenResult();
    const fromClaims = token?.claims?.tenantId || null;
    if (fromClaims) return fromClaims;
  } catch (e) {}
  // Fallback: leer tenantId desde el documento del usuario en Firestore
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const tid = userDoc.data()?.tenantId || null;
      if (tid) return tid;
    }
  } catch (e) {}
  return __cachedTenantId;
};

export const getCachedTenantId = () => __cachedTenantId;

// (Eliminado: Trial / Licencias)

// ===== Pendientes de aprobación (wrappers usados por AdminDayDashboard) =====
export const fetchPendingUsersViaFunction = async (explicitTenantId) => {
  try {
    // Evitar llamadas a HTTP Functions que disparan CORS en algunos entornos.
    // Leemos directamente desde Firestore con filtros por tenant y estado.
    const tenantId = explicitTenantId || getCachedTenantId();
    const list = await getPendingUsersOnce(tenantId);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    console.warn('fetchPendingUsersViaFunction fallo:', e?.message || e);
    return [];
  }
};

export const getPendingUsersOnce = async (explicitTenantId) => {
  try {
    const tenantId = explicitTenantId || getCachedTenantId();
    if (!tenantId) return [];
    const [q1, q2, q3] = await Promise.all([
      getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId), where('approvalStatus', '==', 'pending'))),
      getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId), where('approvalStatus', '==', 'pendiente'))),
      getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId), where('approved', '==', false)))
    ]);
    const map = new Map();
    q1.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
    q2.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
    q3.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
    const list = Array.from(map.values());
    list.sort((a, b) => {
      const ta = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate().getTime() : 0;
      const tb = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate().getTime() : 0;
      return tb - ta;
    });
    return list;
  } catch (e) {
    console.warn('getPendingUsersOnce fallo:', e?.message || e);
    return [];
  }
};

export const getRecentUsersAndFilterPending = async (maxCount = 200, explicitTenantId) => {
  try {
    const tenantId = explicitTenantId || getCachedTenantId();
    if (!tenantId) return [];
    // Leer solo del tenant actual
    const snap = await getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId)));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const pend = all.filter((u) => {
      const status = (u.approvalStatus || '').toString().toLowerCase();
      const approved = u.approved === false;
      return status === 'pending' || status === 'pendiente' || approved;
    });
    pend.sort((a, b) => {
      const ta = a.createdAt && a.createdAt.toDate ? a.createdAt.toDate().getTime() : 0;
      const tb = b.createdAt && b.createdAt.toDate ? b.createdAt.toDate().getTime() : 0;
      return tb - ta;
    });
    return pend.slice(0, maxCount);
  } catch (e) {
    console.warn('getRecentUsersAndFilterPending fallo:', e?.message || e);
    return [];
  }
};

// Autenticación
export const signUp = async (email, password, displayName, phone, tenantId = null) => {
  try {
    // Validación de entrada
    if (!email || !password || !displayName) {
      throw new Error('Email, contraseña y nombre son requeridos');
    }
    
    if (!email.includes('@') || !email.includes('.')) {
      throw new Error('Email inválido');
    }
    
    if (password.length < 6) {
      throw new Error('La contraseña debe tener al menos 6 caracteres');
    }
    
    if (displayName.trim().length < 2) {
      throw new Error('El nombre debe tener al menos 2 caracteres');
    }
    
    if (phone && !/^[+]?[0-9\s\-()]{9,}$/.test(phone)) {
      throw new Error('Número de teléfono inválido');
    }
    
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    
    // Crear documento de usuario en Firestore
    let requireApproval = false;
    try {
      // Si se proporciona tenantId, usar configuración específica del tenant
      if (tenantId) {
        const tenantRef = doc(db, 'appConfigTenants', tenantId);
        const tenantDoc = await getDoc(tenantRef);
        if (tenantDoc.exists()) {
          const data = tenantDoc.data();
          requireApproval = !!data.requireAdminApproval;
        }
      } else {
        // Fallback a configuración global
      const cfg = await getAppConfig();
      requireApproval = !!cfg.requireAdminApproval;
      }
    } catch {}
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      uid: userCredential.user.uid,
      email: email,
      displayName: displayName,
      phone: phone,
      isAdmin: false,
      approved: requireApproval ? false : true,
      approvalStatus: requireApproval ? 'pending' : 'approved',
      createdAt: serverTimestamp(),
      appointments: []
    });
    
    // Si se requiere aprobación, cerrar sesión y avisar al caller
    if (requireApproval) {
      try { await signOut(auth); } catch {}
      return { pendingApproval: true };
    }
    
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const signIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error;
  }
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Verificar si el usuario es admin
export const checkIfAdmin = async (uid) => {
  try {
    if (!uid || typeof uid !== 'string') {
      console.log('UID inválido:', uid);
      return false;
    }
    
    // Verificar que el UID no esté vacío y tenga formato válido
    if (uid.trim().length === 0 || uid.length < 10) {
      console.log('UID con formato inválido:', uid);
      return false;
    }
    
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      // Verificar que el usuario no esté baneado
      if (userData.isBanned === true) {
        console.log('Usuario baneado:', uid);
        return false;
      }
      return userData && userData.isAdmin === true;
    }
    return false;
  } catch (error) {
    // Si hay error de permisos, asumir que no es admin
    if (error.code === 'permission-denied') {
      console.log('Usuario nuevo o sin permisos, asumiendo no admin');
      return false;
    }
    console.error('Error verificando admin:', error);
    return false;
  }
};

// Servicios de Firestore
export const createAppointment = async (appointmentData) => {
  try {
    // Validación de entrada
    if (!appointmentData || typeof appointmentData !== 'object') {
      throw new Error('Datos de cita inválidos');
    }
    
    const requiredFields = ['userId', 'userName', 'userEmail', 'date', 'time', 'type', 'price'];
    const missingFields = requiredFields.filter(field => !appointmentData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
    }
    
    console.log('📝 Creando cita con datos:', appointmentData);
    const tenantId = await getActiveTenantId();
    const docRef = await addDoc(collection(db, 'appointments'), {
      ...appointmentData,
      tenantId: tenantId || appointmentData.tenantId || null,
      createdAt: serverTimestamp(),
      // Nuevo estado por defecto en español
      status: 'pendiente'
    });
    console.log('✅ Cita creada exitosamente con ID:', docRef.id);
    try {
      trackEvent('appointment_created', {
        tenantId: tenantId || appointmentData.tenantId || null,
        price: Number(appointmentData.price) || 0,
        type: appointmentData.type || 'Servicio',
        employeeId: appointmentData.employeeId || null,
        serviceId: appointmentData.serviceId || null,
        duration: Number(appointmentData.duration) || null,
        has_employee: !!appointmentData.employeeId
      });
    } catch {}
    
    // Registrar en historial del usuario
    if (appointmentData.userId) {
      try {
        console.log('🔍 === INICIO REGISTRO HISTORIAL ===');
        console.log('📝 Registrando en historial del usuario:', appointmentData.userId);
        console.log('📋 Datos de la cita:', {
          type: appointmentData.type,
          date: appointmentData.date,
          time: appointmentData.time,
          duration: appointmentData.duration,
          price: appointmentData.price
        });
        
        // Construir detalles evitando valores undefined
        const detailsClean = {};
        if (appointmentData.type !== undefined) {
          detailsClean['Tipo de servicio'] = appointmentData.type;
        }
        if (appointmentData.date !== undefined) {
          detailsClean['Fecha'] = appointmentData.date?.toDate
            ? appointmentData.date.toDate().toLocaleDateString('es-ES')
            : appointmentData.date;
        }
        if (appointmentData.time !== undefined) {
          detailsClean['Hora'] = appointmentData.time;
        }
        if (appointmentData.duration !== undefined) {
          detailsClean['Duración'] = `${appointmentData.duration} minutos`;
        }
        if (appointmentData.price !== undefined) {
          detailsClean['Precio'] = `€${appointmentData.price}`;
        }

        const historyResult = await addUserHistoryEntry(appointmentData.userId, {
          type: 'appointment_created',
          title: 'Nueva cita creada',
          description: `Cita programada para ${appointmentData.type}`,
          details: detailsClean
        });
        
        console.log('✅ Historial registrado exitosamente:', historyResult);
        
        // Actualizar estadísticas del usuario
        console.log('📊 Actualizando estadísticas del usuario...');
        const statsResult = await updateUserStats(appointmentData.userId, {
          appointments: 1,
          lastVisit: appointmentData.date
        });
        
        console.log('✅ Estadísticas actualizadas exitosamente:', statsResult);
        console.log('🔍 === FIN REGISTRO HISTORIAL ===');
      } catch (historyError) {
        console.error('❌ === ERROR EN HISTORIAL ===');
        console.error('Error registrando historial:', historyError);
        console.error('Detalles del error:', {
          userId: appointmentData.userId,
          error: historyError.message,
          stack: historyError.stack
        });
        console.error('=== FIN ERROR ===');
        // No fallar la creación de la cita por error en historial
      }
    } else {
      console.warn('⚠️ No hay userId para registrar historial');
      console.log('🔍 appointmentData completo:', appointmentData);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creando cita:', error);
    throw error;
  }
};

export const getAppointments = async (userId = null, maxCount = 100) => {
  try {
    const tenantId = await getActiveTenantId();
    let q;
    if (userId && typeof userId === 'string') {
      q = query(
        collection(db, 'appointments'), 
        where('userId', '==', userId),
        orderBy('date', 'desc'),
        fsLimit(maxCount)
      );
    } else {
      q = query(
        collection(db, 'appointments'), 
        orderBy('date', 'desc'),
        fsLimit(maxCount)
      );
    }
    if (tenantId) q = query(q, where('tenantId', '==', tenantId));
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo citas:', error);
    throw new Error('Error al obtener las citas. Por favor, inténtalo de nuevo.');
  }
};

export const updateAppointment = async (appointmentId, updates) => {
  try {
    // Obtener la cita actual para comparar cambios
    const appointmentDoc = await getDoc(doc(db, 'appointments', appointmentId));
    if (!appointmentDoc.exists()) {
      throw new Error('Cita no encontrada');
    }
    
    const currentAppointment = appointmentDoc.data();
    
    // Recalcular TTL si cambia fecha/hora
    try {
      const willUpdateDate = updates?.date || updates?.dateStr || updates?.time;
      if (willUpdateDate) {
        let baseDate = null;
        if (updates.date && updates.date.toDate) baseDate = updates.date.toDate();
        else if (updates.date instanceof Date) baseDate = updates.date;
        else if ((updates.dateStr || currentAppointment?.dateStr) && (updates.time || currentAppointment?.time)) {
          const ds = String(updates.dateStr || currentAppointment?.dateStr);
          const tm = String(updates.time || currentAppointment?.time);
          const [y, mo, d] = ds.split('-').map(n => parseInt(n, 10));
          const [hh, mm] = tm.split(':').map(n => parseInt(n, 10));
          baseDate = new Date(y || 1970, (mo || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
        } else if (currentAppointment?.date?.toDate) {
          baseDate = currentAppointment.date.toDate();
        }
        if (baseDate && !isNaN(baseDate.getTime())) {
          const ttl = new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          updates.ttlAt = ttl;
        }
      }
    } catch {}

    // Actualizar la cita
    await updateDoc(doc(db, 'appointments', appointmentId), updates);
    try {
      const newStatus = (updates?.status || '').toString().toLowerCase();
      const baseParams = {
        tenantId: currentAppointment?.tenantId || null,
        appointmentId,
        new_status: newStatus || undefined,
        has_notes: typeof updates?.notes === 'string' ? updates.notes.length > 0 : undefined,
        duration: typeof updates?.duration === 'number' ? updates.duration : undefined,
        price: typeof updates?.price === 'number' ? updates.price : undefined
      };
      if (newStatus === 'completada' || newStatus === 'completed') {
        trackEvent('appointment_completed', baseParams);
      } else if (newStatus === 'cancelada' || newStatus === 'cancelled') {
        trackEvent('appointment_cancelled', baseParams);
      } else if (Object.keys(updates || {}).length > 0) {
        trackEvent('appointment_updated', { ...baseParams, changed: Object.keys(updates || {}) });
      }
    } catch {}
    
            // Registrar cambio en historial del usuario
        if (currentAppointment.userId) {
          try {
            let historyType = 'appointment_updated';
            let title = 'Cita actualizada';
            let description = 'Se modificaron los detalles de la cita';
            
            // Determinar tipo de cambio
            const statusUpdate = updates.status;
            const isCompleted = statusUpdate === 'completed' || statusUpdate === 'completada';
            const isCancelled = statusUpdate === 'cancelled' || statusUpdate === 'cancelada';
            const isNoShow = statusUpdate === 'no_show' || statusUpdate === 'no_asiste';

            if (isCompleted) {
              historyType = 'service_completed';
              title = 'Servicio completado';
              description = 'El servicio ha sido completado exitosamente';
              
              // REGISTRAR EN CONTABILIDAD cuando se complete una cita
              try {
                await addDoc(collection(db, 'accounting'), {
                  type: 'income',
                  category: (currentAppointment.type || 'Servicios'),
                  description: `${currentAppointment.serviceName || currentAppointment.type || 'Servicio'}`,
                  amount: currentAppointment.price || 0,
                  date: currentAppointment.date,
                  appointmentId: appointmentId,
                  employeeId: currentAppointment.employeeId || currentAppointment.employee?.id || currentAppointment.staffId || null,
                  employeeName: currentAppointment.employeeName || currentAppointment.employee?.name || currentAppointment.staffName || null,
                  userId: currentAppointment.userId,
                  createdAt: serverTimestamp(),
                  source: 'appointment_completion'
                });
                console.log(`💰 Ingreso registrado en contabilidad: €${currentAppointment.price}`);
              } catch (accountingError) {
                console.error('❌ Error registrando en contabilidad:', accountingError);
                // No fallar la actualización por error en contabilidad
              }
              
            } else if (isCancelled) {
              historyType = 'appointment_cancelled';
              title = 'Cita cancelada';
              description = 'La cita ha sido cancelada';
            } else if (isNoShow) {
              historyType = 'appointment_missed';
              title = 'Cita no asistida';
              description = 'El cliente no se presentó a la cita programada';
            }
            
            await addUserHistoryEntry(currentAppointment.userId, {
              type: historyType,
              title: title,
              description: description,
              details: {
                'Tipo de servicio': currentAppointment.type,
                'Fecha': currentAppointment.date.toDate ? currentAppointment.date.toDate().toLocaleDateString('es-ES') : currentAppointment.date,
                'Hora': currentAppointment.time,
                'Estado anterior': currentAppointment.status,
                'Estado nuevo': updates.status || currentAppointment.status,
                'Cambios': Object.keys(updates).join(', ')
              }
            });
            
            // Actualizar estadísticas según el cambio
            if (isCompleted) {
              await updateUserStats(currentAppointment.userId, {
                completed: 1,
                spent: currentAppointment.price || 0
              });
            } else if (isCancelled) {
              await updateUserStats(currentAppointment.userId, {
                cancelled: 1
              });
            }
          } catch (historyError) {
            console.warn('⚠️ Error registrando historial:', historyError);
            // No fallar la actualización por error en historial
          }
        }
  } catch (error) {
    throw error;
  }
};

export const deleteAppointment = async (appointmentId) => {
  try {
    await deleteDoc(doc(db, 'appointments', appointmentId));
  } catch (error) {
    throw error;
  }
};

export const createAccountingEntry = async (entryData) => {
  try {
    const tid = await getActiveTenantId();
    const docRef = await addDoc(collection(db, 'accounting'), {
      ...entryData,
      tenantId: tid || entryData.tenantId || null,
      source: entryData.source || 'manual_entry',
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const getAccountingEntries = async (type = null, startDate = null, endDate = null) => {
  try {
    let q = collection(db, 'accounting');
    
    if (type) {
      q = query(q, where('type', '==', type));
    }
    
    if (startDate && endDate) {
      q = query(q, where('date', '>=', startDate), where('date', '<=', endDate));
    }
    
    q = query(q, orderBy('date', 'desc'));
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

export const createInventoryItem = async (itemData) => {
  try {
    const tid = await getActiveTenantId();
    const docRef = await addDoc(collection(db, 'inventory'), {
      ...itemData,
      tenantId: tid || null,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  } catch (error) {
    throw error;
  }
};

export const getInventoryItems = async () => {
  try {
    const tid = await getActiveTenantId();
    let q = collection(db, 'inventory');
    if (tid) q = query(q, where('tenantId', '==', tid));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

export const updateInventoryItem = async (itemId, updates) => {
  try {
    await updateDoc(doc(db, 'inventory', itemId), updates);
  } catch (error) {
    throw error;
  }
};

// ===== UTILIDAD ADMIN: REINICIAR DATOS DE RESERVAS =====
export const resetReservationData = async () => {
  try {
    const results = {
      appointmentsDeleted: 0,
      accountingDeleted: 0,
      usersHistoryUpdated: 0
    };

    // 1) Borrar todas las citas
    const deleteCollection = async (colRef) => {
      const snapshot = await getDocs(colRef);
      if (snapshot.empty) return 0;
      let count = 0;
      // Procesar en lotes de ~400 para margen
      const docs = snapshot.docs;
      for (let i = 0; i < docs.length; i += 400) {
        const batchDocs = docs.slice(i, i + 400);
        const batch = (await import('firebase/firestore')).writeBatch(db);
        batchDocs.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        count += batchDocs.length;
      }
      return count;
    };

    results.appointmentsDeleted = await deleteCollection(collection(db, 'appointments'));

    // 2) Borrar apuntes de contabilidad derivados de citas (por "source")
    const deleteAccountingBySource = async (sourceValue) => {
      const qRef = query(collection(db, 'accounting'), where('source', '==', sourceValue));
      const snap = await getDocs(qRef);
      if (snap.empty) return 0;
      let count = 0;
      for (let i = 0; i < snap.docs.length; i += 400) {
        const batchDocs = snap.docs.slice(i, i + 400);
        const batch = (await import('firebase/firestore')).writeBatch(db);
        batchDocs.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        count += batchDocs.length;
      }
      return count;
    };

    results.accountingDeleted += await deleteAccountingBySource('appointment_completion');
    results.accountingDeleted += await deleteAccountingBySource('auto_completion');
    results.accountingDeleted += await deleteAccountingBySource('manual_completion');

    // 2b) Borrar cualesquiera apuntes que tengan appointmentId (por seguridad)
    try {
      const allAccSnap = await getDocs(collection(db, 'accounting'));
      const toDelete = allAccSnap.docs.filter(d => {
        const data = d.data() || {};
        return !!data.appointmentId;
      });
      for (let i = 0; i < toDelete.length; i += 400) {
        const slice = toDelete.slice(i, i + 400);
        const batch = (await import('firebase/firestore')).writeBatch(db);
        slice.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        results.accountingDeleted += slice.length;
      }
    } catch (_) {}

    // 3) Limpiar historial de usuarios relacionado con citas
    const usersSnap = await getDocs(collection(db, 'users'));
    const batchSize = 300;
    const allUsers = usersSnap.docs;
    for (let i = 0; i < allUsers.length; i += batchSize) {
      const slice = allUsers.slice(i, i + batchSize);
      const batch = (await import('firebase/firestore')).writeBatch(db);
      slice.forEach((userDoc) => {
        const data = userDoc.data() || {};
        const entries = data.historyEntries || {};
        const retained = {};
        Object.values(entries).forEach((entry) => {
          const t = (entry && entry.type) || '';
          const isAppointmentRelated = (
            t === 'appointment_created' ||
            t === 'appointment_updated' ||
            t === 'appointment_cancelled' || // cancelada
            t === 'appointment_missed' ||    // no_asiste
            t === 'service_completed' ||
            t === 'service_cancelled' ||     // por compatibilidad futura
            t === 'service_no_show'          // por compatibilidad futura
          );
          if (!isAppointmentRelated && entry && entry.id) {
            retained[entry.id] = entry;
          }
        });
        batch.update(userDoc.ref, {
          historyEntries: retained,
          history: Object.keys(retained).length
        });
      });
      await batch.commit();
      results.usersHistoryUpdated += slice.length;
    }

    return { ok: true, ...results };
  } catch (error) {
    console.error('Error reseteando datos de reservas:', error);
    return { ok: false, error: error.message };
  }
};

// ===== COPIAS DE SEGURIDAD (EXPORT/IMPORT) =====
const serializeForBackup = (value) => {
  try {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) return value.map(serializeForBackup);
    if (value && typeof value === 'object') {
      // Firestore Timestamp
      if (value.toDate && typeof value.toDate === 'function' && typeof value.seconds === 'number') {
        return { __timestamp: true, seconds: value.seconds, nanoseconds: value.nanoseconds };
      }
      // Plain object
      const out = {};
      Object.entries(value).forEach(([k, v]) => {
        out[k] = serializeForBackup(v);
      });
      return out;
    }
    return value;
  } catch {
    return value;
  }
};

const reviveFromBackup = async (value) => {
  const { Timestamp } = await import('firebase/firestore');
  const revive = (val) => {
    if (val === null || val === undefined) return val;
    if (Array.isArray(val)) return val.map(revive);
    if (val && typeof val === 'object') {
      if (val.__timestamp === true && typeof val.seconds === 'number') {
        return new Timestamp(val.seconds, val.nanoseconds || 0);
      }
      const out = {};
      Object.entries(val).forEach(([k, v]) => {
        out[k] = revive(v);
      });
      return out;
    }
    return val;
  };
  return revive(value);
};

export const exportAppData = async () => {
  try {
    const [usersSnap, apptSnap, accSnap] = await Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'appointments')),
      getDocs(collection(db, 'accounting'))
    ]);

    const users = usersSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    const appointments = apptSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    const accounting = accSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));

    return {
      meta: {
        exportedAt: new Date().toISOString(),
        version: 1
      },
      users,
      appointments,
      accounting
    };
  } catch (error) {
    console.error('Error exportando datos:', error);
    throw error;
  }
};

// Export por tenant (usuarios, citas, servicios, empleados)
export const exportTenantData = async (tenantId) => {
  if (!tenantId) throw new Error('tenantId requerido');
  try {
    const [usersSnap, apptSnap, servicesSnap, empSnap] = await Promise.all([
      getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId))),
      getDocs(query(collection(db, 'appointments'), where('tenantId', '==', tenantId))),
      getDocs(query(collection(db, 'services'), where('tenantId', '==', tenantId))),
      getDocs(query(collection(db, 'employees'), where('tenantId', '==', tenantId)))
    ]);
    const users = usersSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    const appointments = apptSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    const services = servicesSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    const employees = empSnap.docs.map(d => serializeForBackup({ id: d.id, ...d.data() }));
    return {
      meta: { exportedAt: new Date().toISOString(), version: 1, tenantId },
      users,
      appointments,
      services,
      employees
    };
  } catch (error) {
    console.error('Error exportando tenant:', error);
    throw error;
  }
};

// Export completo (todos los tenants) desde cliente
export const exportAllTenantsData = async () => {
  try {
    // Intentar leer desde 'businesses' y si no existe, usar 'tenants'
    let tenantDocs = [];
    try {
      const bizSnap = await getDocs(collection(db, 'businesses'));
      tenantDocs = bizSnap.docs;
    } catch (_) {}
    if (tenantDocs.length === 0) {
      try {
        const tenSnap = await getDocs(collection(db, 'tenants'));
        tenantDocs = tenSnap.docs;
      } catch (_) {}
    }
    const tenantIds = tenantDocs.map(d => d.id || d.data()?.tenantId).filter(Boolean);
    const byTenant = {};
    for (const tid of tenantIds) {
      try {
        byTenant[tid] = await exportTenantData(tid);
      } catch (e) {
        byTenant[tid] = { meta: { tenantId: tid }, error: e?.message || String(e) };
      }
    }
    return {
      meta: { exportedAt: new Date().toISOString(), version: 1, tenants: tenantIds.length },
      tenants: byTenant
    };
  } catch (error) {
    console.error('Error exportando todos los tenants:', error);
    throw error;
  }
};

export const importAppData = async (backupJson) => {
  try {
    const data = await reviveFromBackup(backupJson);
    const { writeBatch } = await import('firebase/firestore');

    const writeDocs = async (colName, docs) => {
      if (!Array.isArray(docs) || docs.length === 0) return 0;
      let count = 0;
      for (let i = 0; i < docs.length; i += 400) {
        const slice = docs.slice(i, i + 400);
        const batch = writeBatch(db);
        slice.forEach((docData) => {
          const id = docData.id || docData.uid || undefined;
          if (id) {
            const { id: _omit, ...rest } = docData;
            batch.set(doc(db, colName, id), rest, { merge: true });
          } else {
            // Si no hay id, creamos uno nuevo
            const newRef = doc(collection(db, colName));
            batch.set(newRef, docData, { merge: true });
          }
        });
        await batch.commit();
        count += slice.length;
      }
      return count;
    };

    const usersCount = await writeDocs('users', data.users || []);
    const apptCount = await writeDocs('appointments', data.appointments || []);
    const accCount = await writeDocs('accounting', data.accounting || []);

    return { ok: true, users: usersCount, appointments: apptCount, accounting: accCount };
  } catch (error) {
    console.error('Error importando datos:', error);
    return { ok: false, error: error.message };
  }
};

export const deleteInventoryItem = async (itemId) => {
  try {
    await deleteDoc(doc(db, 'inventory', itemId));
  } catch (error) {
    throw error;
  }
};

export const getUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (userId, updates) => {
  try {
    // Sanitizar: eliminar undefined y normalizar 'notes' a string
    const payload = {};
    Object.keys(updates || {}).forEach((k) => {
      const v = updates[k];
      if (v === undefined) return; // Firestore no acepta undefined
      if (k === 'notes') {
        payload[k] = (v ?? '').toString();
      } else {
        payload[k] = v;
      }
    });
    await updateDoc(doc(db, 'users', userId), payload);
  } catch (error) {
    throw error;
  }
};

// Listeners en tiempo real
// Normalización de estados a español (compatibilidad hacia atrás)
const normalizeStatus = (status, cancelledFlag) => {
  if (cancelledFlag === true) return 'cancelada';
  switch (status) {
    case 'confirmed':
    case 'pending':
      return 'pendiente';
    case 'completed':
      return 'completada';
    case 'no_show':
      return 'no_asiste';
    case 'cancelled':
      return 'cancelada';
    default:
      return status || 'pendiente';
  }
};

export const subscribeToAppointments = (callback, userId = null) => {
  // Traer SIEMPRE por usuario (si se pasa) sin filtrar por tenant en la query
  // y filtrar en memoria para incluir documentos legacy sin tenantId
  let q;
  if (userId && typeof userId === 'string') {
    q = query(collection(db, 'appointments'), where('userId', '==', userId));
  } else {
    q = query(collection(db, 'appointments'), orderBy('date', 'asc'));
  }

  return onSnapshot(q, async (snapshot) => {
    const activeTenantId = getCachedTenantId();
    const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const filtered = items.filter(d => {
      if (!activeTenantId) return true;
      const tid = typeof d.tenantId === 'string' ? d.tenantId : '';
      return tid === activeTenantId || tid === '';
    }).map(data => ({
      ...data,
      status: normalizeStatus(data.status, data.cancelled)
    }));
    callback(filtered);
  });
};

// ===== Empleados =====
export const addEmployee = async (employee) => {
  try {
    console.log('🔄 addEmployee - Datos recibidos:', employee);
    const tenantId = getCachedTenantId();
    console.log('🔄 addEmployee - TenantId:', tenantId);
    
    const ref = doc(collection(db, 'employees'));
    const employeeData = { 
      ...employee, 
      active: employee.active !== false, 
      createdAt: serverTimestamp(),
      tenantId: tenantId
    };
    console.log('🔄 addEmployee - Datos a guardar:', employeeData);
    
    await setDoc(ref, employeeData);
    console.log('✅ addEmployee - Empleado guardado con ID:', ref.id);
    return ref.id;
  } catch (error) {
    console.error('❌ addEmployee - Error:', error);
    throw error;
  }
};

export const updateEmployee = async (employeeId, updates) => {
  await updateDoc(doc(db, 'employees', employeeId), updates);
};

export const deleteEmployee = async (employeeId) => {
  await deleteDoc(doc(db, 'employees', employeeId));
};

export const getActiveEmployees = async () => {
  const tenantId = await getActiveTenantId();
  let q = query(collection(db, 'employees'), where('active', '==', true));
  if (tenantId) q = query(q, where('tenantId', '==', tenantId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeToEmployees = (callback) => {
  let q = query(collection(db, 'employees'));
  const tenantId = getCachedTenantId();
  if (tenantId) q = query(q, where('tenantId', '==', tenantId));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (error) => {
    // Manejar específicamente errores de permisos
    if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
      console.log('🔍 Permisos insuficientes para acceder a datos (esto es normal para clientes)');
      callback([]); // Devolver array vacío en caso de error de permisos
    } else {
      console.error('❌ Error en suscripción:', error);
    }
  });
};

// ===== Reserva vía Cloud Function (transaccional) =====
export const bookSlotViaFunction = async (payload) => {
  try {
    console.log('🔄 bookSlotViaFunction - Payload recibido:', payload);
    
    // No validar límite en cliente; delegar en Cloud Function para evitar desajustes
    
    // payload debe incluir: dateStr, time, serviceId, y opcionalmente employeeId, type, price, duration, userName, userEmail
    const fn = httpsCallable(functions, 'createAppointmentSafely');
    console.log('🔄 bookSlotViaFunction - Llamando a createAppointmentSafely...');
    const res = await fn(payload);
    console.log('✅ bookSlotViaFunction - Respuesta recibida:', res.data);
    try {
      if (res?.data?.ok) {
        // Registrar evento de creación de cita cuando la función transaccional confirma OK
        trackEvent('appointment_created', {
          tenantId: await getActiveTenantId(),
          serviceId: payload?.serviceId || null,
          employeeId: payload?.employeeId || null,
          type: payload?.type || 'Servicio',
          duration: Number(payload?.duration) || null,
          price: Number(payload?.price) || 0,
          source: 'callable'
        });
      }
    } catch {}
    return res.data;
  } catch (error) {
    console.error('❌ bookSlotViaFunction - Error:', error);
    throw error;
  }
};

// Verificar límite de citas por usuario
export const checkUserAppointmentLimit = async (userId) => {
  try {
    const tenantId = await getActiveTenantId();
    
    if (!tenantId) {
      console.warn('⚠️ No hay tenantId disponible para verificar límite de citas');
      return {
        currentCount: 0,
        maxAllowed: 3,
        canBook: true,
        activeAppointments: []
      };
    }

    const appointmentsRef = collection(db, 'appointments');
    const q = query(
      appointmentsRef,
      where('userId', '==', userId),
      where('tenantId', '==', tenantId),
      where('status', '==', 'pendiente')
    );
    
    const snapshot = await getDocs(q);
    const now = new Date();
    const activeAppointments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      .filter((apt) => {
        try {
          const d = apt.date?.toDate ? apt.date.toDate() : (apt.date ? new Date(apt.date) : null);
          const start = d || (apt.dateStr && apt.time ? new Date(parseInt(apt.dateStr.split('-')[0],10), parseInt(apt.dateStr.split('-')[1],10)-1, parseInt(apt.dateStr.split('-')[2],10), parseInt(apt.time.split(':')[0],10), parseInt(apt.time.split(':')[1],10), 0, 0) : null);
          return !!(start && start > now);
        } catch { return false; }
      });
    
    // Obtener configuración de límite máximo del tenant
    let maxAppointments = 3; // valor por defecto
    if (tenantId) {
      const tenantConfigDoc = await getDoc(doc(db, 'appConfigTenants', tenantId));
      if (tenantConfigDoc.exists()) {
        const raw = tenantConfigDoc.data().maxAppointmentsPerUser;
        const n = parseInt(raw, 10);
        maxAppointments = isNaN(n) ? 3 : Math.min(Math.max(1, n), 10);
      }
    } else {
      const configDoc = await getDoc(doc(db, 'appConfig', 'main'));
      if (configDoc.exists()) {
        const raw = configDoc.data().maxAppointmentsPerUser;
        const n = parseInt(raw, 10);
        maxAppointments = isNaN(n) ? 3 : Math.min(Math.max(1, n), 10);
      } else {
        maxAppointments = 3;
      }
    }
    
    return {
      currentCount: activeAppointments.length,
      maxAllowed: maxAppointments,
      canBook: activeAppointments.length < maxAppointments,
      activeAppointments
    };
  } catch (error) {
    console.error('Error verificando límite de citas:', error);
    return {
      currentCount: 0,
      maxAllowed: 3,
      canBook: true,
      activeAppointments: []
    };
  }
};

// ===== Callables de respaldo para citas =====
export const fetchMyAppointmentsViaFunction = async () => {
  try {
    const fn = httpsCallable(functions, 'getMyAppointments');
    const res = await fn();
    const items = (res && res.data && Array.isArray(res.data.items)) ? res.data.items : [];
    return items;
  } catch (e) {
    console.warn('fetchMyAppointmentsViaFunction fallo:', e?.message || e);
    return [];
  }
};

export const fetchTenantAppointmentsViaFunction = async () => {
  try {
    const fn = httpsCallable(functions, 'getTenantAppointments');
    const res = await fn();
    const items = (res && res.data && Array.isArray(res.data.items)) ? res.data.items : [];
    return items;
  } catch (e) {
    console.warn('fetchTenantAppointmentsViaFunction fallo:', e?.message || e);
    return [];
  }
};

// HTTP robusto: obtiene citas del usuario autenticado usando ID token
export const fetchMyAppointmentsHttp = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];
    const idToken = await user.getIdToken(true);
    const res = await fetch('https://europe-west1-gestion-integral-unas.cloudfunctions.net/getMyAppointmentsHttp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + idToken,
      },
      body: JSON.stringify({})
    });
    const json = await res.json().catch(() => ({}));
    return (json && json.items) ? json.items : (json?.data?.items || []);
  } catch (e) {
    console.warn('fetchMyAppointmentsHttp fallo:', e?.message || e);
    return [];
  }
};

// Función específica para administradores - obtener todas las citas
export const subscribeToAllAppointments = (callback) => {
  console.log('🔍 Admin: Iniciando suscripción a todas las citas');
  let unsubscribe = () => {};
  (async () => {
    try {
      const tid = await getActiveTenantId();
      console.log('🔍 Admin: TenantId obtenido:', tid);
      if (!tid) {
        console.warn('⚠️ tenantId no disponible aún; se omitirá la suscripción de citas.');
        return;
      }
      
      // Verificar TODAS las citas en la BD (sin filtro de tenant)
      console.log('🔍 Admin: Verificando TODAS las citas en la BD...');
      const allAppointmentsQuery = collection(db, 'appointments');
      onSnapshot(allAppointmentsQuery, (snapshot) => {
        console.log('🔍 Admin: TODAS las citas en BD:', snapshot.docs.length);
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log('🔍 Admin: Cita ID:', doc.id);
          console.log('🔍 Admin: tenantId:', data.tenantId);
          console.log('🔍 Admin: date:', data.date);
          console.log('🔍 Admin: time:', data.time);
          console.log('🔍 Admin: clientName:', data.clientName);
          console.log('🔍 Admin: serviceName:', data.serviceName);
          console.log('🔍 Admin: status:', data.status);
          console.log('🔍 Admin: userId:', data.userId);
          console.log('🔍 Admin: employeeId:', data.employeeId);
          console.log('---');
        });
      }, (error) => {
        // Manejar específicamente errores de permisos
        if (error.code === 'permission-denied' || 
            error.message?.includes('Missing or insufficient permissions') ||
            error.message?.includes('permission-denied')) {
          console.log('🔍 Admin: Permisos insuficientes para acceder a todas las citas (esto es normal para clientes no aprobados)');
        } else {
          console.error('❌ Admin: Error cargando todas las citas:', error);
        }
      });
      
      // Solo citas del tenant actual; las legacy sin tenantId se corrigen con backfill
      let q = query(
        collection(db, 'appointments'),
        where('tenantId', '==', tid)
      );
      console.log('🔍 Admin: Query de citas construida:', q);
      const startIndexedListener = () => onSnapshot(q, async (snapshot) => {
        console.log('🔍 Admin: Snapshot de citas recibido:', snapshot.docs.length, 'documentos');
        console.log('🔍 Admin: Documentos raw:', snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() })));
        
        // Obtener datos completos de cada cita incluyendo información del usuario y empleado
        const appointments = await Promise.all(snapshot.docs.map(async (snapDoc) => {
          const data = snapDoc.data();
          let userData = {};
          let employeeData = {};
          
          // Si hay userId, obtener datos del usuario
          if (data.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', data.userId));
              if (userDoc.exists()) {
                const userInfo = userDoc.data();
                // Obtener las notas del usuario desde userNotes (sistema complejo)
                let userNotes = '';
                if (userInfo.userNotes && typeof userInfo.userNotes === 'object') {
                  // Formato complejo: objeto con múltiples notas
                  const notesArray = Object.values(userInfo.userNotes)
                    .map(note => note.content)
                    .filter(content => content && content.trim());
                  userNotes = notesArray.join('; ');
                }
                
                userData = {
                  userPhone: userInfo.phone || '',
                  userNotes: userNotes || '',
                  userDisplayName: userInfo.displayName || data.userName || ''
                };
              }
            } catch (error) {
              // Manejar específicamente errores de permisos
              if (error.code === 'permission-denied' || 
                  error.message?.includes('Missing or insufficient permissions') ||
                  error.message?.includes('permission-denied') ||
                  (error.code && error.code.includes('permission'))) {
                console.log('Permisos insuficientes para acceder a datos del usuario:', data.userId);
                // Usar datos básicos de la cita si no se pueden obtener datos del usuario
                userData = {
                  userPhone: data.userPhone || '',
                  userNotes: data.userNotes || '',
                  userDisplayName: data.userName || data.clientName || ''
                };
              } else {
                console.warn('Error obteniendo datos del usuario:', error);
                // Usar datos básicos como fallback
                userData = {
                  userPhone: data.userPhone || '',
                  userNotes: data.userNotes || '',
                  userDisplayName: data.userName || data.clientName || ''
                };
              }
            }
          }
          
          // Si hay employeeId, obtener datos del empleado
          if (data.employeeId) {
            try {
              const employeeDoc = await getDoc(doc(db, 'employees', data.employeeId));
              if (employeeDoc.exists()) {
                const employeeInfo = employeeDoc.data();
                employeeData = {
                  employeeColor: employeeInfo.color || '#3B82F6',
                  employeeDisplayName: employeeInfo.displayName || data.employeeName || ''
                };
              }
            } catch (error) {
              // Manejar específicamente errores de permisos
              if (error.code === 'permission-denied' || 
                  error.message?.includes('Missing or insufficient permissions') ||
                  error.message?.includes('permission-denied') ||
                  (error.code && error.code.includes('permission'))) {
                console.log('Permisos insuficientes para acceder a datos del empleado:', data.employeeId);
                // Usar datos básicos de la cita si no se pueden obtener datos del empleado
                employeeData = {
                  employeeColor: '#3B82F6',
                  employeeDisplayName: data.employeeName || 'Empleado'
                };
              } else {
                console.warn('Error obteniendo datos del empleado:', error);
                // Usar datos básicos como fallback
                employeeData = {
                  employeeColor: '#3B82F6',
                  employeeDisplayName: data.employeeName || 'Empleado'
                };
              }
            }
          }
          
          return {
            id: snapDoc.id,
            ...snapDoc.data(),
            ...userData,
            ...employeeData,
            status: normalizeStatus(snapDoc.data().status, snapDoc.data().cancelled)
          };
        }));
        
        // Ordenar en cliente por fecha ascendente
        const sorted = appointments.sort((a, b) => {
          const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
          const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
          return ad - bd;
        });
        console.log('🔍 Admin: Citas procesadas:', sorted);
        callback(sorted);
      }, (error) => {
        console.error('❌ Admin: Error en snapshot (intentando sin índice):', error);
        // Fallback mientras el índice se construye: sin orderBy, y ordenamos en cliente
        try { unsubscribe(); } catch {}
        const qFallback = query(collection(db, 'appointments'), where('tenantId', '==', tid));
        unsubscribe = onSnapshot(qFallback, async (snapshot) => {
          // Obtener datos completos de cada cita incluyendo información del usuario
          const appointments = await Promise.all(snapshot.docs.map(async (snapDoc) => {
            const data = snapDoc.data();
            let userData = {};
            
            // Si hay userId, obtener datos del usuario
            if (data.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', data.userId));
                if (userDoc.exists()) {
                  const userInfo = userDoc.data();
                  // Compat: leer notas desde userNotes (objeto) o notes (string)
                  let mergedNotes = '';
                  if (userInfo.userNotes && typeof userInfo.userNotes === 'object') {
                    mergedNotes = Object.values(userInfo.userNotes)
                      .map(n => n && n.content)
                      .filter(Boolean)
                      .join('; ');
                  }

                  userData = {
                    userPhone: userInfo.phone || '',
                    userNotes: mergedNotes || '',
                    userDisplayName: userInfo.displayName || data.userName || ''
                  };
                }
              } catch (error) {
                // Manejar específicamente errores de permisos
                if (error.code === 'permission-denied' || 
                    error.message?.includes('Missing or insufficient permissions') ||
                    error.message?.includes('permission-denied') ||
                    (error.code && error.code.includes('permission'))) {
                  console.log('Permisos insuficientes para acceder a datos del usuario:', data.userId);
                } else {
                  console.warn('Error obteniendo datos del usuario:', error);
                }
              }
            }
            
            return {
              id: snapDoc.id,
              ...snapDoc.data(),
              ...userData,
              status: normalizeStatus(doc.data().status, doc.data().cancelled)
            };
          }));
          
          const sortedAppointments = appointments.sort((a, b) => {
              const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
              const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
              return ad - bd;
            });
          
          callback(sortedAppointments);
        }, (error) => {
          // Manejar específicamente errores de permisos
          if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
            console.log('🔍 Admin: Permisos insuficientes para acceder a citas (esto es normal para clientes)');
            callback([]); // Devolver array vacío en caso de error de permisos
          } else {
            console.error('❌ Admin: Error en suscripción fallback:', error);
          }
        });
      });
      unsubscribe = startIndexedListener();
    } catch (e) {
      console.error('❌ Admin: Error preparando suscripción:', e);
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// Función específica para clientes - obtener solo citas de su tenantId
export const subscribeToTenantAppointments = (callback) => {
  console.log('🔍 Client: Iniciando suscripción a citas del tenant');
  let unsubscribe = () => {};
  (async () => {
    try {
      const tid = await getActiveTenantId();
      console.log('🔍 Client: TenantId obtenido:', tid);
      if (!tid) {
        console.warn('⚠️ tenantId no disponible aún; se omitirá la suscripción de citas.');
        return;
      }
      
      // Solo citas del tenant actual; las legacy se corrigen con backfill
      const q = query(
        collection(db, 'appointments'),
        where('tenantId', '==', tid)
      );
      console.log('🔍 Client: Query de citas construida:', q);
      
      unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('📥 Client: Snapshot recibido con', snapshot.docs.length, 'docs');
        
        const appointments = snapshot.docs.map(snapDoc => ({
          id: snapDoc.id,
          ...snapDoc.data(),
          status: normalizeStatus(snapDoc.data().status, snapDoc.data().cancelled)
        }));
        
        // Ordenar en el cliente para evitar problemas de índices
        const sortedAppointments = appointments.sort((a, b) => {
          const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
          const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
          return ad - bd;
        });
        
        callback(sortedAppointments);
      }, (error) => {
        // Manejar específicamente errores de permisos
        if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
          console.log('🔍 Client: Permisos insuficientes para acceder a citas');
          callback([]); // Devolver array vacío en caso de error de permisos
        } else {
          console.error('❌ Client: Error en suscripción:', error);
          callback([]); // Devolver array vacío en caso de cualquier error
        }
      });
    } catch (e) {
      console.error('❌ Client: Error preparando suscripción:', e);
      callback([]); // Devolver array vacío en caso de error
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// Suscripción a citas por empleado (admin)
export const subscribeToAppointmentsByEmployee = (employeeId, callback) => {
  if (!employeeId) return () => {};
  let unsubscribe = () => {};
  (async () => {
    try {
      const tid = await getActiveTenantId();
      // Base query por tenant y empleado
      const base = tid
        ? query(collection(db, 'appointments'), where('tenantId', '==', tid), where('employeeId', '==', employeeId))
        : query(collection(db, 'appointments'), where('employeeId', '==', employeeId));
      const ordered = query(base, orderBy('date', 'desc'));
      const start = () => onSnapshot(ordered, (snapshot) => {
        const list = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
            const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
            return bd - ad;
          });
        callback(list);
      }, (error) => {
        if (error?.code === 'failed-precondition' || /requires an index|index is currently building/i.test(error?.message || '')) {
          // Fallback sin orderBy
          try { unsubscribe(); } catch {}
          unsubscribe = onSnapshot(base, (snapshot) => {
            const list = snapshot.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .sort((a, b) => {
                const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
                const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
                return bd - ad;
              });
            callback(list);
          });
        } else if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
          console.log('🔍 Permisos insuficientes para acceder a citas por empleado');
          callback([]);
        } else {
          console.error('❌ Error en suscripción citas por empleado:', error);
          callback([]);
        }
      });
      unsubscribe = start();
    } catch (e) {
      console.error('❌ Error iniciando suscripción citas por empleado:', e);
      callback([]);
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

export const subscribeToAccounting = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    const tid = await getActiveTenantId();
    if (!tid) { callback([]); return; }
    const base = query(collection(db, 'accounting'), where('tenantId', '==', tid));
    const ordered = query(base, orderBy('date', 'desc'));
    const start = () => onSnapshot(ordered, (snapshot) => {
      const entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(entries);
    }, (error) => {
      if (error?.code === 'failed-precondition' || /requires an index|index is currently building/i.test(error?.message || '')) {
        try { unsubscribe(); } catch {}
        // Fallback sin orderBy, ordenar en cliente
        unsubscribe = onSnapshot(base, (snapshot) => {
          const entries = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => {
              const ad = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
              const bd = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
              return bd - ad;
            });
          callback(entries);
        });
      } else {
        console.error('Error en snapshot de contabilidad:', error);
        callback([]);
      }
    });
    unsubscribe = start();
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// Suscripción a estadísticas mensuales agregadas para gráficos
export const subscribeToAccountingStats = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    try {
      const tid = await getActiveTenantId();
      if (!tid) { callback({ income: Array(12).fill(0), expense: Array(12).fill(0) }); return; }
      const currentYear = new Date().getFullYear();
      const colRef = collection(db, 'accounting_stats');
      const qRef = query(colRef, where('tenantId', '==', tid), where('year', '==', currentYear));
      unsubscribe = onSnapshot(qRef, (snap) => {
        const income = Array(12).fill(0);
        const expense = Array(12).fill(0);
        snap.docs.forEach((d) => {
          const s = d.data();
          const m = Math.min(11, Math.max(0, Number(s.month || 0)));
          income[m] = Number(s.income || 0);
          expense[m] = Number(s.expense || 0);
        });
        callback({ income, expense });
      }, (e) => {
        console.warn('subscribeToAccountingStats error:', e);
        callback({ income: Array(12).fill(0), expense: Array(12).fill(0) });
      });
    } catch (e) {
      console.error('Error preparando suscripción de stats:', e);
      callback({ income: Array(12).fill(0), expense: Array(12).fill(0) });
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// ===== Categorías de Contabilidad =====
export const subscribeToAccountingCategories = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    const tid = await getActiveTenantId();
    if (!tid) { callback({ income: [], expense: [] }); return; }
    const col = collection(db, 'accounting_categories');
    const q = query(col, where('tenantId', '==', tid));
    unsubscribe = onSnapshot(q, (snap) => {
      const docData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Estructura: { income: string[], expense: string[] }
      const base = { income: ['Servicios', 'Productos', 'Otros'], expense: ['Suministros', 'Alquiler', 'Servicios', 'Otros'] };
      if (docData.length > 0) {
        const d = docData[0];
        callback({
          income: Array.isArray(d.income) ? d.income : base.income,
          expense: Array.isArray(d.expense) ? d.expense : base.expense,
          _docId: d.id
        });
      } else {
        callback(base);
      }
    }, (e) => {
      console.warn('subscribeToAccountingCategories error:', e);
      callback({ income: ['Servicios', 'Productos', 'Otros'], expense: ['Suministros', 'Alquiler', 'Servicios', 'Otros'] });
    });
  })();
  return () => { try { unsubscribe(); } catch {} };
};

export const upsertAccountingCategories = async (data) => {
  const tid = await getActiveTenantId();
  if (!tid) throw new Error('No tenant');
  const col = collection(db, 'accounting_categories');
  const q = query(col, where('tenantId', '==', tid));
  const snap = await getDocs(q);
  const payload = {
    tenantId: tid,
    income: Array.isArray(data.income) ? data.income : [],
    expense: Array.isArray(data.expense) ? data.expense : [],
    updatedAt: serverTimestamp(),
  };
  if (snap.empty) {
    await addDoc(col, { ...payload, createdAt: serverTimestamp() });
  } else {
    await updateDoc(doc(db, 'accounting_categories', snap.docs[0].id), payload);
  }
};

// Función para migrar citas existentes sin tenantId
// (Eliminado) migrateAppointmentsWithoutTenantId: ya no es necesario.

export const subscribeToInventory = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    try {
      const tid = await getActiveTenantId();
      if (!tid) { callback([]); return; }
      const base = query(collection(db, 'inventory'), where('tenantId', '==', tid));
      const ordered = query(base, orderBy('name', 'asc'));
      const start = () => onSnapshot(ordered, (snapshot) => {
        const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(items);
      }, (error) => {
        if (error?.code === 'failed-precondition' || /requires an index|index is currently building/i.test(error?.message || '')) {
          console.warn('⚠️ Índice de inventario en construcción. Fallback sin orderBy.');
          try { unsubscribe(); } catch {}
          unsubscribe = onSnapshot(base, (snapshot) => {
            const items = snapshot.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .sort((a, b) => (String(a.name || '').localeCompare(String(b.name || ''))));
            callback(items);
          });
        } else {
          console.error('Error en snapshot de inventario:', error);
          callback([]);
        }
      });
      unsubscribe = start();
    } catch (e) {
      console.error('Error preparando suscripción de inventario:', e);
      callback([]);
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// ===== Reportes / Utilidades de consultas =====
export const getAllEmployees = async () => {
  const snap = await getDocs(collection(db, 'employees'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getMonthAppointments = async (year, month) => {
  try {
    const start = new Date(year, month, 1, 0, 0, 0, 0);
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
    let qRef = query(
      collection(db, 'appointments'),
      where('date', '>=', start),
      where('date', '<=', end)
    );
    try {
      const tid = await getActiveTenantId();
      if (tid) {
        qRef = query(qRef, where('tenantId', '==', tid));
      }
    } catch {}
    const snap = await getDocs(qRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('Error obteniendo citas del mes:', e);
    return [];
  }
};

export const getAppointmentsInRange = async (startDate, endDate) => {
  try {
    const qRef = query(
      collection(db, 'appointments'),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    const snap = await getDocs(qRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('Error obteniendo citas en rango:', e);
    return [];
  }
};

// ===== GESTIÓN DE HISTORIAL DE USUARIOS =====

// Suscripción a todos los usuarios (mapa id -> usuario)
export const subscribeToUsersMap = (callback) => {
  let q = query(collection(db, 'users'));
  const tenantId = getCachedTenantId();
  if (tenantId) q = query(q, where('tenantId', '==', tenantId));
  return onSnapshot(q, (snapshot) => {
    const map = {};
    snapshot.docs.forEach(docu => {
      map[docu.id] = { id: docu.id, ...docu.data() };
    });
    callback(map);
  }, (error) => {
    // Manejar específicamente errores de permisos
    if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
      console.log('🔍 Permisos insuficientes para acceder a datos (esto es normal para clientes)');
      callback({}); // Devolver objeto vacío en caso de error de permisos
    } else {
      console.error('❌ Error en suscripción:', error);
    }
  });
};

// Suscripción a usuarios pendientes de aprobación (admins)
export const subscribeToPendingUsers = (callback, explicitTenantId) => {
  const cached = explicitTenantId || getCachedTenantId();
  if (!cached) {
    // Hasta conocer tenantId, no exponemos datos
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, 'users'),
    where('tenantId', '==', cached),
    where('approvalStatus', '==', 'pending')
  );
  return onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (error) => {
    if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
      console.log('🔍 Permisos insuficientes para acceder a datos (esto es normal para clientes)');
      callback([]);
    } else {
      console.error('❌ Error en suscripción:', error);
    }
  });
};

// Aprobar usuario (admins) - Usa Cloud Function para actualizar tanto Firestore como Auth claims
export const approveUser = async (userId) => {
  const approveUserFunction = httpsCallable(functions, 'approveUser');
  const result = await approveUserFunction({ userId });
  return result.data;
};

// Función para forzar actualización de claims de un usuario específico
export const forceRefreshUserClaims = async (email) => {
  const forceRefreshUserClaims = httpsCallable(functions, 'forceRefreshUserClaims');
  const result = await forceRefreshUserClaims({ email });
  return result.data;
};

// Denegar usuario (admins)
export const denyUser = async (userId) => {
  const refUser = doc(db, 'users', userId);
  await updateDoc(refUser, {
    approved: false,
    approvalStatus: 'denied',
    deniedAt: serverTimestamp()
  });
};

// Eliminar usuario por admin (Cloud Function callable)
export const deleteUserByAdmin = async (userId) => {
  const fn = httpsCallable(functions, 'deleteUserByAdmin');
  const res = await fn({ userId });
  return res.data;
};

// Agregar entrada al historial de un usuario
export const addUserHistoryEntry = async (userId, entry) => {
  try {
    console.log(`🔍 === INICIO addUserHistoryEntry ===`);
    console.log(`📝 Agregando entrada de historial para usuario ${userId}:`, entry);
    
    const userRef = doc(db, 'users', userId);
    console.log('📍 Referencia del usuario creada:', userRef.path);
    
    const userDoc = await getDoc(userRef);
    console.log('📄 Documento del usuario obtenido:', userDoc.exists());
    
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado');
    }
    
    console.log('✅ Usuario encontrado, agregando entrada de historial...');
    console.log('📋 Datos del usuario:', userDoc.data());
    
    // Limpieza profunda para remover valores undefined/null en cualquier nivel
    const deepCleanUndefined = (value) => {
      if (Array.isArray(value)) {
        return value
          .map((v) => deepCleanUndefined(v))
          .filter((v) => v !== undefined && v !== null);
      }
      if (value && typeof value === 'object') {
        const cleaned = {};
        Object.entries(value).forEach(([k, v]) => {
          const cleanedValue = deepCleanUndefined(v);
          if (cleanedValue !== undefined && cleanedValue !== null && !(typeof cleanedValue === 'object' && !Array.isArray(cleanedValue) && Object.keys(cleanedValue).length === 0)) {
            cleaned[k] = cleanedValue;
          }
        });
        return cleaned;
      }
      return value === undefined ? null : value;
    };

    // Construir entrada y limpiar
    const historyEntryRaw = {
      ...entry,
      timestamp: serverTimestamp(),
      id: Date.now().toString() // ID único para la entrada
    };
    const historyEntry = deepCleanUndefined(historyEntryRaw);
    
    console.log('📝 Entrada de historial preparada:', historyEntry);
    
    // Agregar al array de historial
    const updateData = {
      history: increment(1),
      [`historyEntries.${historyEntry.id}`]: historyEntry
    };
    
    console.log('📤 Datos a actualizar:', updateData);
    
    await updateDoc(userRef, updateData);
    
    console.log(`✅ Entrada de historial agregada exitosamente con ID: ${historyEntry.id}`);
    console.log(`🔍 === FIN addUserHistoryEntry ===`);
    return historyEntry;
  } catch (error) {
    console.error('❌ === ERROR EN addUserHistoryEntry ===');
    console.error('Error agregando entrada al historial:', error);
    console.error('Detalles del error:', {
      userId,
      entry,
      error: error.message,
      stack: error.stack
    });
    console.error('=== FIN ERROR ===');
    throw error;
  }
};

// Backfill: asegurar que el historial del usuario tenga entradas básicas por cada cita
export const backfillUserHistoryFromAppointments = async (userId) => {
  try {
    if (!userId) return { created: 0 };
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return { created: 0 };

    const existing = userDoc.data().historyEntries || {};
    const existingKeys = new Set(Object.keys(existing));

    // Obtener todas las citas del usuario
    const apptSnap = await getDocs(query(collection(db, 'appointments'), where('userId', '==', userId)));
    if (apptSnap.empty) return { created: 0 };

    const updateData = {};
    let createdCount = 0;

    apptSnap.forEach((docSnap) => {
      const appt = docSnap.data();
      const apptId = docSnap.id;

      const ensureEntry = (eventType, title, description, details) => {
        const key = `hist_appt_${apptId}_${eventType}`;
        if (existingKeys.has(key)) return;
        updateData[`historyEntries.${key}`] = {
          id: key,
          type: eventType,
          title,
          description,
          details,
          timestamp: serverTimestamp()
        };
        createdCount += 1;
      };

      // Siempre: creación
      ensureEntry(
        'appointment_created',
        'Nueva cita creada',
        `Cita programada para ${appt.type || 'servicio'}`,
        {
          'Tipo de servicio': appt.type,
          'Fecha': appt.date?.toDate ? appt.date.toDate().toLocaleDateString('es-ES') : appt.date,
          'Hora': appt.time,
          'Duración': appt.duration ? `${appt.duration} minutos` : undefined,
          'Precio': appt.price != null ? `€${appt.price}` : undefined
        }
      );

      // Según estado
      const status = appt.status;
      if (status === 'completed') {
        ensureEntry(
          'service_completed',
          'Servicio completado',
          'El servicio ha sido completado exitosamente',
          {
            'Tipo de servicio': appt.type,
            'Fecha': appt.date?.toDate ? appt.date.toDate().toLocaleDateString('es-ES') : appt.date,
            'Hora': appt.time
          }
        );
      } else if (status === 'cancelled') {
        ensureEntry(
          'appointment_cancelled',
          'Cita cancelada',
          'La cita ha sido cancelada',
          {
            'Tipo de servicio': appt.type,
            'Fecha': appt.date?.toDate ? appt.date.toDate().toLocaleDateString('es-ES') : appt.date,
            'Hora': appt.time
          }
        );
      } else if (status === 'no_show') {
        ensureEntry(
          'appointment_missed',
          'Cita no asistida',
          'El cliente no se presentó a la cita programada',
          {
            'Tipo de servicio': appt.type,
            'Fecha programada': appt.date?.toDate ? appt.date.toDate().toLocaleDateString('es-ES') : appt.date,
            'Hora programada': appt.time
          }
        );
      }
    });

    if (createdCount > 0) {
      // Limpiar valores indefinidos
      const clean = (obj) => {
        const out = {};
        Object.entries(obj).forEach(([k, v]) => {
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            const nested = clean(v);
            if (Object.keys(nested).length > 0) out[k] = nested;
          } else if (v !== undefined) {
            out[k] = v;
          }
        });
        return out;
      };

      const cleanedUpdate = clean(updateData);
      cleanedUpdate.history = increment(createdCount);
      await updateDoc(userRef, cleanedUpdate);
    }

    return { created: createdCount };
  } catch (error) {
    console.error('Error backfilling user history:', error);
    return { created: 0, error: error.message };
  }
};

// Obtener historial completo de un usuario
export const getUserHistory = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return [];
    }
    
    const userData = userDoc.data();
    const historyEntries = userData.historyEntries || {};
    
    // Convertir objeto a array y ordenar por timestamp
    const toMillis = (ts) => {
      try {
        if (!ts) return 0;
        if (ts.toDate) return ts.toDate().getTime();
        if (typeof ts.seconds === 'number') return ts.seconds * 1000;
        return 0;
      } catch {
        return 0;
      }
    };

    return Object.values(historyEntries)
      .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp));
  } catch (error) {
    console.error('Error obteniendo historial:', error);
    return [];
  }
};

// Agregar nota médica/anotación a un usuario
export const addUserNote = async (userId, note) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado');
    }
    
    const noteEntry = {
      id: Date.now().toString(),
      content: note.content,
      type: note.type || 'general', // general, medical, preference, etc.
      createdAt: serverTimestamp(),
      createdBy: note.createdBy || 'admin'
    };
    
    // Agregar al array de notas
    await updateDoc(userRef, {
      notes: increment(1),
      [`userNotes.${noteEntry.id}`]: noteEntry
    });
    
    return noteEntry;
  } catch (error) {
    console.error('Error agregando nota:', error);
    throw error;
  }
};

// Obtener notas de un usuario
export const getUserNotes = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return [];
    }
    
    const userData = userDoc.data();
    const notes = userData.userNotes || {};
    
    // Convertir objeto a array y ordenar por fecha
    return Object.values(notes)
      .sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.toDate() - a.createdAt.toDate();
        }
        return 0;
      });
  } catch (error) {
    console.error('Error obteniendo notas:', error);
    return [];
  }
};

// Actualizar estadísticas del usuario
export const updateUserStats = async (userId, statsUpdate) => {
  try {
    console.log(`🔍 === INICIO updateUserStats ===`);
    console.log(`📊 Actualizando estadísticas para usuario ${userId}:`, statsUpdate);
    
    const userRef = doc(db, 'users', userId);
    console.log('📍 Referencia del usuario para estadísticas:', userRef.path);
    
    const updateData = {
      totalAppointments: increment(statsUpdate.appointments || 0),
      totalSpent: increment(statsUpdate.spent || 0),
      completedServices: increment(statsUpdate.completed || 0),
      cancelledAppointments: increment(statsUpdate.cancelled || 0),
      lastVisit: statsUpdate.lastVisit || serverTimestamp()
    };
    
    console.log('📤 Datos de estadísticas a actualizar:', updateData);
    
    await updateDoc(userRef, updateData);
    
    console.log(`✅ Estadísticas actualizadas exitosamente para usuario ${userId}`);
    console.log(`🔍 === FIN updateUserStats ===`);
  } catch (error) {
    console.error('❌ === ERROR EN updateUserStats ===');
    console.error('Error actualizando estadísticas:', error);
    console.error('Detalles del error:', {
      userId,
      statsUpdate,
      error: error.message,
      stack: error.stack
    });
    console.error('=== FIN ERROR ===');
    throw error;
  }
};

// Marcar citas pasadas como completadas automáticamente
export const markPastAppointments = async () => {
  // Usar callable en backend para evitar problemas de permisos con reglas
  try {
    const fn = httpsCallable(functions, 'markPastAppointmentsCallable');
    const res = await fn({});
    return res.data || { ok: false };
  } catch (error) {
    console.error('❌ Error marcando citas pasadas (callable):', error);
    throw error;
  }
};

// Marcar cita como completada manualmente
export const markAppointmentAsCompleted = async (appointmentId) => {
  try {
    const appointmentRef = doc(db, 'appointments', appointmentId);
    const appointmentDoc = await getDoc(appointmentRef);
    
    if (!appointmentDoc.exists()) {
      throw new Error('Cita no encontrada');
    }
    
    const appointment = appointmentDoc.data();
    
    // Actualizar estado a completada
    await updateDoc(appointmentRef, {
      status: 'completada',
      updatedAt: serverTimestamp(),
      completedAt: serverTimestamp()
    });
    
    // Registrar en contabilidad
    try {
      const tid = await getActiveTenantId();
      await addDoc(collection(db, 'accounting'), {
        type: 'income',
        category: (appointment.type || 'Servicios'),
        description: `${appointment.serviceName || appointment.type || 'Servicio'}`,
        amount: appointment.price || 0,
        price: appointment.price || 0,
        date: appointment.date,
        appointmentId: appointmentId,
        employeeId: appointment.employeeId || appointment.employee?.id || appointment.staffId || null,
        employeeName: appointment.employeeName || appointment.employee?.name || appointment.staffName || null,
        userId: appointment.userId,
        tenantId: tid || appointment.tenantId || null,
        createdAt: serverTimestamp(),
        source: 'manual_completion'
      });
      console.log(`💰 Ingreso registrado en contabilidad: €${appointment.price}`);
    } catch (accountingError) {
      console.error('❌ Error registrando en contabilidad:', accountingError);
    }
    
    // Registrar en historial del usuario
    if (appointment.userId) {
      try {
        await addUserHistoryEntry(appointment.userId, {
          type: 'service_completed',
          title: 'Servicio completado',
          description: 'El servicio ha sido completado exitosamente',
          details: {
            'Tipo de servicio': appointment.type,
            'Fecha': appointment.date.toDate ? appointment.date.toDate().toLocaleDateString('es-ES') : appointment.date,
            'Hora': appointment.time,
            'Estado anterior': normalizeStatus(appointment.status, appointment.cancelled),
            'Estado nuevo': 'completada',
            'Completado manualmente': 'Sí'
          }
        });
        
        // Actualizar estadísticas del usuario
        await updateUserStats(appointment.userId, {
          completed: 1,
          spent: appointment.price || 0
        });
      } catch (historyError) {
        console.warn('⚠️ Error actualizando historial:', historyError);
      }
    }
    
    return { success: true, appointmentId };
  } catch (error) {
    console.error('❌ Error marcando cita como completada:', error);
    throw error;
  }
};

// ===== GESTIÓN DE CONFIGURACIÓN DE LA APLICACIÓN =====

// Obtener configuración de la aplicación
export const getAppConfig = async () => {
  try {
    const tenantId = await getActiveTenantId();
    // 1) Intentar config específica del tenant
    if (tenantId) {
      const tenantRef = doc(db, 'appConfigTenants', tenantId);
      const tenantDoc = await getDoc(tenantRef);
      if (tenantDoc.exists()) {
        const data = tenantDoc.data();
        return data; // Devolver la configuración tal como está almacenada
      }
      // crear por defecto para el tenant si no existe
      const defaultTenantConfig = {
        appName: 'Reserberry',
        logoUrl: '',
        welcomeMessage: '',
        welcomeMessageEnabled: false,
        primaryColor: '#8b5cf6',
        secondaryColor: '#ec4899',
        requireAdminApproval: false,
        maxAppointmentsPerUser: 1,
        tenantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(tenantRef, defaultTenantConfig, { merge: true });
      return defaultTenantConfig;
    }

    // 2) Fallback: config global
    const configRef = doc(db, 'appConfig', 'main');
    const configDoc = await getDoc(configRef);
    if (configDoc.exists()) {
      const data = configDoc.data();
      return data; // Devolver la configuración tal como está almacenada
    }
    const defaultConfig = {
      appName: 'Reserberry',
      logoUrl: '',
      welcomeMessage: '',
      welcomeMessageEnabled: false,
      primaryColor: '#8b5cf6',
      secondaryColor: '#ec4899',
      requireAdminApproval: false,
      maxAppointmentsPerUser: 1,
      updatedAt: serverTimestamp()
    };
    await setDoc(configRef, defaultConfig);
    return defaultConfig;
  } catch (error) {
    console.error('Error obteniendo configuración:', error);
    return {
      appName: 'Reserberry',
      logoUrl: '',
      welcomeMessage: '',
      welcomeMessageEnabled: false,
      primaryColor: '#8b5cf6',
      secondaryColor: '#ec4899',
      requireAdminApproval: false
    };
  }
};

// Actualizar configuración de la aplicación
export const updateAppConfig = async (config) => {
  try {
    const tenantId = await getActiveTenantId();
    // Normalizar límite de citas (1-10)
    const maxAptsRaw = config?.maxAppointmentsPerUser;
    let maxApts = parseInt(maxAptsRaw, 10);
    if (isNaN(maxApts)) maxApts = 3;
    maxApts = Math.min(Math.max(1, maxApts), 10);
    const normalized = { ...config, maxAppointmentsPerUser: maxApts };
    if (tenantId) {
      const configRef = doc(db, 'appConfigTenants', tenantId);
      await setDoc(configRef, { ...normalized, tenantId, updatedAt: serverTimestamp() }, { merge: true });
    } else {
      const configRef = doc(db, 'appConfig', 'main');
      await setDoc(configRef, { ...normalized, updatedAt: serverTimestamp() }, { merge: true });
    }
    console.log('✅ Configuración actualizada:', normalized);
    return true;
  } catch (error) {
    console.error('❌ Error actualizando configuración:', error);
    throw error;
  }
};

// Subscribirse a cambios de configuración
export const subscribeToAppConfig = (callback) => {
  let unsubscribe = () => {};
  (async () => {
    const tenantId = await getActiveTenantId();
    if (tenantId) {
      const configRef = doc(db, 'appConfigTenants', tenantId);
      unsubscribe = onSnapshot(configRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          callback(data); // Devolver la configuración tal como está almacenada
        }
      });
      return;
    }
    // Fallback a global si no hay tenant
    const globalRef = doc(db, 'appConfig', 'main');
    unsubscribe = onSnapshot(globalRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        callback(data); // Devolver la configuración tal como está almacenada
      }
    });
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// Funciones para controlar disponibilidad de fechas
export const setDateAvailable = async (date, slots = []) => {
  try {
    const tenantId = await getActiveTenantId();
    const dateStr = format(date, 'yyyy-MM-dd');
    const docId = `${tenantId || 'global'}__${dateStr}`;
    const docRef = doc(db, 'availableDates', docId);
    
    // Capacidad por defecto = nº de empleados activos
    let activeCount = 1;
    let activeIds = [];
    try {
      let q = query(collection(db, 'employees'), where('active', '==', true));
      if (tenantId) q = query(q, where('tenantId', '==', tenantId));
      const snap = await getDocs(q);
      activeCount = snap.size || 1;
      activeIds = snap.docs.map(d => d.id);
    } catch {}

    // Normalizar slots: aceptar strings u objetos → guardar objetos { time, capacity, employees }
    const normalizedSlots = (Array.isArray(slots) ? slots : []).map((s) => {
      if (typeof s === 'string') return { time: s, capacity: activeCount, employees: [] };
      if (s && typeof s === 'object') {
        const blocked = Array.isArray(s.employees) ? s.employees : [];
        const allowed = activeIds.filter(id => !blocked.includes(id));
        const cap = s.capacity != null
          ? Math.min(
              Math.max(Number(s.capacity), 0),
              Math.max(1, (allowed.length || activeCount))
            )
          : Math.max(1, (allowed.length || activeCount));
        // Guardamos permitidos si no son todos; si todos permitidos, guardamos [] para indicar "todos"
        return { time: s.time, capacity: cap, employees: (allowed.length > 0 && allowed.length < activeIds.length) ? allowed : [] };
      }
      return null;
    }).filter(Boolean);

    await setDoc(docRef, {
      date: dateStr,
      tenantId: tenantId || null,
      available: true,
      defaultCapacity: activeCount,
      slots: normalizedSlots,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    console.log(`✅ Fecha ${dateStr} marcada como disponible`);
  } catch (error) {
    console.error('Error marcando fecha como disponible:', error);
    throw error;
  }
};

export const setDateUnavailable = async (date) => {
  try {
    const tenantId = await getActiveTenantId();
    const dateStr = format(date, 'yyyy-MM-dd');
    const docId = `${tenantId || 'global'}__${dateStr}`;
    const docRef = doc(db, 'availableDates', docId);
    
    await setDoc(docRef, {
      date: dateStr,
      tenantId: tenantId || null,
      available: false,
      slots: [],
      maxBookingsPerSlot: 0,
      updatedAt: serverTimestamp()
    });
    
    console.log(`❌ Fecha ${dateStr} marcada como no disponible`);
  } catch (error) {
    console.error('Error marcando fecha como no disponible:', error);
    throw error;
  }
};

export const getAvailableDates = async (startDate, endDate) => {
  try {
    const tenantId = await getActiveTenantId();
    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');
    
    let q = query(
      collection(db, 'availableDates'),
      where('date', '>=', startStr),
      where('date', '<=', endStr),
      where('available', '==', true)
    );
    if (tenantId) q = query(q, where('tenantId', '==', tenantId));
    
    const querySnapshot = await getDocs(q);
    const availableDates = {};
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      let normalizedSlots = [];
      if (Array.isArray(data.slots)) {
        normalizedSlots = data.slots.map((s) => {
          if (typeof s === 'string') return { time: s, capacity: data.defaultCapacity || data.maxBookingsPerSlot || 1, employees: [] };
          if (s && typeof s === 'object') return { time: s.time, capacity: s.capacity ?? (data.defaultCapacity || data.maxBookingsPerSlot || 1), employees: Array.isArray(s.employees) ? s.employees : [] };
          return null;
        }).filter(Boolean);
      }
      availableDates[data.date] = {
        slots: normalizedSlots,
        defaultCapacity: data.defaultCapacity || 1,
        maxBookingsPerSlot: data.maxBookingsPerSlot || 1
      };
    });
    
    return availableDates;
  } catch (error) {
    console.error('Error obteniendo fechas disponibles:', error);
    throw error;
  }
};

export const subscribeToAvailableDates = (callback, startDate, endDate) => {
  let unsubscribe = () => {};
  (async () => {
    try {
      const tenantId = await getActiveTenantId();
      if (!tenantId) {
        console.warn('⚠️ tenantId no disponible aún; se omitirá la suscripción de fechas.');
        return;
      }
      const startStr = format(startDate, 'yyyy-MM-dd');
      const endStr = format(endDate, 'yyyy-MM-dd');
      
      let q = query(collection(db, 'availableDates'), where('tenantId', '==', tenantId));
      unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const availableDates = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (!data?.date || data.date < startStr || data.date > endStr) return;
          let normalizedSlots = [];
          if (Array.isArray(data.slots)) {
            normalizedSlots = data.slots.map((s) => {
              if (typeof s === 'string') return { time: s, capacity: data.defaultCapacity || data.maxBookingsPerSlot || 1, employees: [] };
              if (s && typeof s === 'object') return { time: s.time, capacity: s.capacity ?? (data.defaultCapacity || data.maxBookingsPerSlot || 1), employees: Array.isArray(s.employees) ? s.employees : [] };
              return null;
            }).filter(Boolean);
          }
          availableDates[data.date] = {
            available: data.available,
            slots: normalizedSlots,
            defaultCapacity: data.defaultCapacity || 1,
            maxBookingsPerSlot: data.maxBookingsPerSlot || 1
          };
        });

        // Sin fallback legacy: evitamos mezclar datos entre negocios
        callback(availableDates);
      }, (error) => {
        console.error('Error suscribiéndose a fechas disponibles:', error);
      });
    } catch (error) {
      console.error('Error preparando suscripción a fechas:', error);
    }
  })();
  return () => { try { unsubscribe(); } catch {} };
};

// Obtener citas que necesitan actualización automática
export const getAppointmentsNeedingUpdate = async () => {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const appointmentsRef = collection(db, 'appointments');
    // Filtrar por tenantId activo para respetar reglas de seguridad y evitar errores de permisos
    const tenantId = await getActiveTenantId().catch(() => null);
    let q = query(
      appointmentsRef,
      where('date', '>=', today),
      where('date', '<', new Date(today.getTime() + 24 * 60 * 60 * 1000))
    );
    if (tenantId) {
      q = query(q, where('tenantId', '==', tenantId));
    }
    q = query(q, where('status', 'in', ['confirmed', 'pendiente']));
    
    const querySnapshot = await getDocs(q);
    const needsUpdate = [];
    
    querySnapshot.forEach((doc) => {
      const appointment = doc.data();
      const appointmentTime = appointment.date.toDate ? appointment.date.toDate() : new Date(appointment.date);
      const appointmentEndTime = new Date(appointmentTime.getTime() + (appointment.duration || 30) * 60000);
      
      if (now > appointmentEndTime) {
        needsUpdate.push({
          id: doc.id,
          ...appointment,
          timeUntilUpdate: Math.floor((now - appointmentEndTime) / (1000 * 60)) // minutos de retraso
        });
      }
    });
    
    return needsUpdate;
  } catch (error) {
    console.error('Error obteniendo citas que necesitan actualización:', error);
    return [];
  }
};
// Disponibilidad efectiva por día (Functions)
export const getEffectiveAvailability = async (dateStr) => {
  const fn = httpsCallable(functions, 'getEffectiveAvailability');
  const res = await fn({ dateStr });
  return res.data; // { ok, dateStr, availableTimes: string[] }
};

// Versión HTTP con CORS explícito
export const getEffectiveAvailabilityHttpClient = async (dateStr) => {
  const user = auth.currentUser;
  if (!user) throw new Error('no_auth');
  const token = await user.getIdToken();
  const url = `https://europe-west1-gestion-integral-unas.cloudfunctions.net/getEffectiveAvailabilityHttp?dateStr=${encodeURIComponent(dateStr)}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return await res.json();
};

// ===== GESTIÓN DE DISPONIBILIDAD =====

// Crear o actualizar disponibilidad para un día
export const setDayAvailability = async (date, availabilityData) => {
  try {
    const dateStr = date.toISOString().split('T')[0];
    const docRef = doc(db, 'availability', dateStr);
    
    await setDoc(docRef, {
      date: dateStr,
      ...availabilityData,
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error configurando disponibilidad:', error);
    throw error;
  }
};

// Obtener disponibilidad de un día específico
export const getDayAvailability = async (date) => {
  try {
    const dateStr = date.toISOString().split('T')[0];
    const docSnap = await getDoc(doc(db, 'availability', dateStr));
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error obteniendo disponibilidad:', error);
    throw error;
  }
};

// Obtener disponibilidad de un rango de fechas
export const getAvailabilityRange = async (startDate, endDate) => {
  try {
    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];
    
    const q = query(
      collection(db, 'availability'),
      where('date', '>=', startStr),
      where('date', '<=', endStr),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo rango de disponibilidad:', error);
    throw error;
  }
};

// Suscribirse a cambios de disponibilidad en tiempo real
export const subscribeToAvailability = (callback, startDate = null, endDate = null) => {
  let q;
  
  if (startDate && endDate) {
    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];
    
    q = query(
      collection(db, 'availability'),
      where('date', '>=', startStr),
      where('date', '<=', endStr),
      orderBy('date', 'asc')
    );
  } else {
    q = query(collection(db, 'availability'), orderBy('date', 'asc'));
  }
  
  return onSnapshot(q, (snapshot) => {
    const availability = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(availability);
  }, (error) => {
    // Manejar específicamente errores de permisos
    if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
      console.log('🔍 Permisos insuficientes para acceder a datos (esto es normal para clientes)');
      callback([]); // Devolver array vacío en caso de error de permisos
    } else {
      console.error('❌ Error en suscripción:', error);
    }
  });
};

// Verificar si un slot está disponible para reserva (SISTEMA NUEVO)
export const checkSlotAvailability = async (date, time, serviceDuration = 30, employeeId = null) => {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const tenantId = await getActiveTenantId();
    // Leer por tenantId + date (sin fallback legacy por id)
    const q = query(collection(db, 'availableDates'), where('tenantId', '==', tenantId), where('date', '==', dateStr));
    const snap = await getDocs(q);
    if (snap.empty) {
      return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
    }
    const dateData = snap.docs[0].data();
    
    // Verificar si la fecha está marcada como disponible
    if (!dateData.available || !dateData.slots || !Array.isArray(dateData.slots)) {
      return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
    }
    
    // Verificar si el horario específico está disponible (compat: string u objeto)
    let slotObj = null;
    if (Array.isArray(dateData.slots)) {
      slotObj = dateData.slots.find((s) => (typeof s === 'string' ? s === time : s?.time === time)) || null;
    }
    if (!slotObj) {
      return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
    }
    
    // Determinar empleados permitidos por slot (si se configuró)
    const allowedEmployees = (typeof slotObj === 'object' && Array.isArray(slotObj.employees) && slotObj.employees.length > 0)
      ? slotObj.employees
      : null; // null => todos permitidos
    if (employeeId && allowedEmployees && !allowedEmployees.includes(employeeId)) {
      return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
    }

    // Si se elige un empleado concreto, exigir que esté activo
    if (employeeId) {
      try {
        const empSnap = await getDoc(doc(db, 'employees', employeeId));
        if (!empSnap.exists() || empSnap.data()?.active === false) {
          return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
        }
      } catch (_) {
        return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
      }
    }

    // Obtener citas existentes para este día
    const appointmentsRef = collection(db, 'appointments');
    let qA = query(
      appointmentsRef,
      where('date', '>=', new Date(dateStr + 'T00:00:00')),
      where('date', '<', new Date(dateStr + 'T23:59:59'))
    );
    if (tenantId) qA = query(qA, where('tenantId', '==', tenantId));
    
    const snapshot = await getDocs(qA);
    // Preferir campo 'time' si existe, para evitar desfases horarios
    const currentBookings = snapshot.docs
      .map(d => d.data())
      .filter(d => (d.cancelled !== true) && (normalizeStatus(d.status, d.cancelled) !== 'cancelada'))
      .filter(d => {
        const t = typeof d.time === 'string' ? d.time : (() => {
          try {
            const aptDate = d.date?.toDate ? d.date.toDate() : new Date(d.date);
            const hh = String(aptDate.getHours()).padStart(2, '0');
            const mm = String(aptDate.getMinutes()).padStart(2, '0');
            return `${hh}:${mm}`;
          } catch { return null; }
        })();
        if (t !== time) return false;
        if (employeeId) return d.employeeId === employeeId; // por empleado concreto
        return true; // indiferente: total
      })
      .length;
    // Capacidad: priorizar capacidad del slot si es objeto; fallback a default
    let maxBookings = dateData.maxBookingsPerSlot || 1;
    if (typeof slotObj === 'object' && slotObj) {
      maxBookings = slotObj.capacity ?? dateData.defaultCapacity ?? maxBookings;
    } else if (dateData.defaultCapacity) {
      maxBookings = dateData.defaultCapacity;
    }
    
    if (employeeId) {
      // Regla: por empleado, una sola reserva por slot
      return { isAvailable: currentBookings < 1, maxBookings: 1, currentBookings };
    }
    return { isAvailable: currentBookings < maxBookings, maxBookings, currentBookings };
  } catch (error) {
    // Fallback: si las reglas bloquean la lectura directa, usar callable getEffectiveAvailability (Admin SDK)
    const msg = String(error?.message || '');
    const code = String(error?.code || '');
    if (code === 'permission-denied' || /Missing or insufficient permissions/i.test(msg)) {
      try {
        const dateStr = format(date, 'yyyy-MM-dd');
        const fn = httpsCallable(functions, 'getEffectiveAvailability');
        const res = await fn({ dateStr });
        const data = res?.data || {};
        const availableTimes = data.availableTimes || [];
        const details = data.details || {};
        const capacities = details.capacities || {};
        const maxBookings = typeof capacities[time] === 'number' ? capacities[time] : 1;
        const isAvailable = availableTimes.includes(time);
        return { isAvailable, maxBookings, currentBookings: isAvailable ? 0 : maxBookings };
      } catch (e) {
        console.warn('Fallback getEffectiveAvailability falló:', e?.message || e);
        return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
      }
    }
    console.error('Error verificando disponibilidad del slot:', error);
    return { isAvailable: false, maxBookings: 0, currentBookings: 0 };
  }
};

// Obtener lista de horarios ya reservados para una fecha (no canceladas)
export const getBookedSlotsForDate = async (date) => {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const appointmentsRef = collection(db, 'appointments');
    let q = query(
      appointmentsRef,
      where('date', '>=', new Date(dateStr + 'T00:00:00')),
      where('date', '<', new Date(dateStr + 'T23:59:59'))
    );
    const tenantId = await getActiveTenantId();
    if (tenantId) q = query(q, where('tenantId', '==', tenantId));
    const snapshot = await getDocs(q);
    const times = new Set();
    snapshot.forEach((docSnap) => {
      const d = docSnap.data();
      // Solo considerar citas ACTIVAS; si no hay status, no contar
      const raw = (d.status || '').toString().toLowerCase().trim();
      const isCancelled = d.cancelled === true || raw === 'cancelada' || raw === 'cancelled';
      const isActive = raw === 'pending' || raw === 'pendiente' || raw === 'confirmed';
      if (isCancelled || !isActive) return;
      let timeStr = undefined;
      // PRIORIDAD: usar campo 'time' si existe para evitar desfases horarios
      if (typeof d.time === 'string' && d.time.includes(':')) {
        try {
          const parts = d.time.split(':');
          const hh = String(parseInt(parts[0] || '0', 10)).padStart(2, '0');
          const mm = String(parseInt(parts[1] || '0', 10)).padStart(2, '0');
          timeStr = `${hh}:${mm}`;
        } catch { timeStr = d.time; }
      }
      if (!timeStr) {
        try {
          const aptDate = d.date?.toDate ? d.date.toDate() : new Date(d.date);
          const hh = String(aptDate.getHours()).padStart(2, '0');
          const mm = String(aptDate.getMinutes()).padStart(2, '0');
          timeStr = `${hh}:${mm}`;
        } catch {}
      }
      if (timeStr) times.add(timeStr);
    });
    return Array.from(times);
  } catch (error) {
    console.error('Error obteniendo slots reservados:', error);
    return [];
  }
};

// Obtener mapa de slots reservados para un rango de fechas [startDate, endDate]
export const getBookedSlotsMapForRange = async (startDate, endDate) => {
  try {
    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');
    const appointmentsRef = collection(db, 'appointments');
    let q = query(
      appointmentsRef,
      where('date', '>=', new Date(startStr + 'T00:00:00')),
      where('date', '<=', new Date(endStr + 'T23:59:59'))
    );
    const tenantId = await getActiveTenantId();
    if (tenantId) q = query(q, where('tenantId', '==', tenantId));
    const snapshot = await getDocs(q);
    const map = {};
    snapshot.forEach((docSnap) => {
      const d = docSnap.data();
      // Solo considerar citas ACTIVAS
      const raw = (d.status || '').toString().toLowerCase().trim();
      const isCancelled = d.cancelled === true || raw === 'cancelada' || raw === 'cancelled';
      const isActive = raw === 'pending' || raw === 'pendiente' || raw === 'confirmed';
      if (isCancelled || !isActive) return;
      let dt;
      try { dt = d.date?.toDate ? d.date.toDate() : new Date(d.date); } catch { dt = null; }
      if (!dt || isNaN(dt.getTime())) return;
      const dateKey = format(dt, 'yyyy-MM-dd');
      let timeStr = undefined;
      // PRIORIDAD: usar 'time' si existe
      if (typeof d.time === 'string' && d.time.includes(':')) {
        try {
          const parts = d.time.split(':');
          const hh = String(parseInt(parts[0] || '0', 10)).padStart(2, '0');
          const mm = String(parseInt(parts[1] || '0', 10)).padStart(2, '0');
          timeStr = `${hh}:${mm}`;
        } catch { timeStr = d.time; }
      }
      if (!timeStr) {
        try {
          const hh = String(dt.getHours()).padStart(2, '0');
          const mm = String(dt.getMinutes()).padStart(2, '0');
          timeStr = `${hh}:${mm}`;
        } catch {}
      }
      if (!timeStr) return;
      if (!map[dateKey]) map[dateKey] = new Set();
      map[dateKey].add(timeStr);
    });
    // Convertir sets a arrays
    const out = {};
    Object.keys(map).forEach((k) => { out[k] = Array.from(map[k]).sort(); });
    return out;
  } catch (error) {
    console.error('Error obteniendo mapa de slots reservados:', error);
    return {};
  }
};

// Incrementar contador de reservas en un slot (SISTEMA NUEVO - NO IMPLEMENTADO)
export const incrementSlotBookings = async (date, time) => {
  try {
    // En el nuevo sistema, las reservas se manejan directamente en la colección appointments
    // No necesitamos mantener contadores separados
    console.log(`✅ Reserva creada para ${format(date, 'yyyy-MM-dd')} a las ${time}`);
    return true;
  } catch (error) {
    console.error('Error incrementando reservas del slot:', error);
    throw error;
  }
};

// Decrementar contador de reservas en un slot (SISTEMA NUEVO - NO IMPLEMENTADO)
export const decrementSlotBookings = async (date, time) => {
  try {
    // En el nuevo sistema, las reservas se manejan directamente en la colección appointments
    // No necesitamos mantener contadores separados
    console.log(`✅ Reserva cancelada para ${format(date, 'yyyy-MM-dd')} a las ${time}`);
    return true;
  } catch (error) {
    console.error('Error decrementando reservas del slot:', error);
    throw error;
  }
};

// ==================== FUNCIONES MULTI-TENANT ====================

// Crear un nuevo tenant (negocio)
export const createTenant = async (tenantData) => {
  try {
    const createTenantFunction = httpsCallable(functions, 'createTenant');
    const result = await createTenantFunction(tenantData);
    return result.data;
  } catch (error) {
    console.error('Error creando tenant:', error);
    throw error;
  }
};

export const createBusiness = async (businessData) => {
  try {
    // 1) Callable directo (sin CORS)
    try {
      const direct = httpsCallable(functions, 'createBusinessFromScratch');
      const res = await direct(businessData);
      if (res?.data?.ok) return res.data;
      console.warn('createBusinessFromScratch fallo:', res?.data);
    } catch (e) {
      console.warn('createBusinessFromScratch error:', e?.message || e);
    }

    // 2) Fallback robusto: comando Firestore procesado por onAdminCommand
    const cmdRef = await addDoc(collection(db, 'adminCommands'), {
      type: 'createBusiness',
      payload: businessData,
      createdAt: new Date(),
      createdBy: auth.currentUser?.uid || null,
      status: 'queued'
    });
    const start = Date.now();
    for (;;) {
      const snap = await getDoc(cmdRef);
      const d = snap.data() || {};
      if (d.status === 'done') return d.result || { ok: true };
      if (d.status === 'error') throw new Error(d.error || 'createBusiness error');
      if (Date.now() - start > 120000) throw new Error('Timeout creando negocio');
      await new Promise(r => setTimeout(r, 1000));
    }
  } catch (error) {
    console.error('Error creando negocio:', error);
    throw error;
  }
};

// Obtener todos los tenants (solo para global admin)
export const getAllTenants = async () => {
  try {
    const getAllTenantsFunction = httpsCallable(functions, 'getAllTenants');
    const result = await getAllTenantsFunction();
    return result.data;
  } catch (error) {
    console.error('Error obteniendo tenants:', error);
    throw error;
  }
};

// Asignar tenant a usuario existente
export const assignTenantToUser = async (userId, tenantId, role = 'client', displayName = null, phone = null) => {
  try {
    const assignTenantFunction = httpsCallable(functions, 'assignTenantToUser');
    const result = await assignTenantFunction({ 
      userId, 
      tenantId, 
      role, 
      displayName, 
      phone 
    });
    return result.data;
  } catch (error) {
    console.error('Error asignando tenant:', error);
    throw error;
  }
};

// Depurar usuarios duplicados por tenant (Cloud Function)
export const dedupeTenantUsers = async (tenantId = null) => {
  const fn = httpsCallable(functions, 'dedupeTenantUsers');
  const res = await fn({ tenantId });
  return res.data;
};

// Migrar datos existentes a tenant por defecto
export const migrateToDefaultTenant = async () => {
  try {
    const migrateFunction = httpsCallable(functions, 'migrateToDefaultTenant');
    const result = await migrateFunction();
    return result.data;
  } catch (error) {
    console.error('Error en migración:', error);
    throw error;
  }
};

// Función helper para agregar tenantId a consultas
export const addTenantFilter = (baseQuery, tenantId) => {
  if (!tenantId) {
    console.warn('⚠️ tenantId no proporcionado para consulta');
    return baseQuery;
  }
  return query(baseQuery, where('tenantId', '==', tenantId));
};

// Función helper para crear documentos con tenantId
export const createDocumentWithTenant = async (collectionName, data, tenantId) => {
  if (!tenantId) {
    throw new Error('tenantId es requerido para crear documentos');
  }
  
  const docData = {
    ...data,
    tenantId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  return await addDoc(collection(db, collectionName), docData);
};

// Función helper para actualizar documentos con tenantId
export const updateDocumentWithTenant = async (collectionName, docId, data, tenantId) => {
  if (!tenantId) {
    throw new Error('tenantId es requerido para actualizar documentos');
  }
  
  const docData = {
    ...data,
    tenantId,
    updatedAt: serverTimestamp()
  };
  
  return await updateDoc(doc(db, collectionName, docId), docData);
};

// ===== Onboarding (por tenant) =====
export const getOnboardingState = async (tenantId) => {
  try {
    if (!tenantId) return null;
    const snap = await getDoc(doc(db, 'appConfigTenants', tenantId));
    if (!snap.exists()) return null;
    return snap.data()?.onboarding || null;
  } catch (_) { return null; }
};

export const setOnboardingState = async (tenantId, onboarding) => {
  if (!tenantId) throw new Error('tenantId requerido');
  try {
    await setDoc(doc(db, 'appConfigTenants', tenantId), { onboarding, updatedAt: serverTimestamp() }, { merge: true });
    return true;
  } catch (e) { console.error('setOnboardingState error', e); return false; }
};

export const subscribeToTenantAppConfigDoc = (tenantId, callback) => {
  if (!tenantId) return () => {};
  try {
    const ref = doc(db, 'appConfigTenants', tenantId);
    return onSnapshot(ref, (snap) => {
      callback(snap.exists() ? (snap.data() || {}) : null);
    }, (error) => {
      if (error.code === 'permission-denied' || (error.message || '').includes('Missing or insufficient permissions')) {
        console.log('🔍 Permisos insuficientes para appConfigTenants');
        callback(null);
      } else {
        console.error('❌ Error en suscripción appConfigTenants:', error);
      }
    });
  } catch (e) {
    console.warn('subscribeToTenantAppConfigDoc error', e);
    return () => {};
  }
};

// Función helper para obtener documentos de un tenant específico
export const getTenantDocuments = async (collectionName, tenantId, orderByField = null) => {
  if (!tenantId) {
    throw new Error('tenantId es requerido para obtener documentos');
  }
  
  let q = collection(db, collectionName);
  q = query(q, where('tenantId', '==', tenantId));
  
  if (orderByField) {
    q = query(q, orderBy(orderByField, 'desc'));
  }
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Eliminar toda la información de un tenant concreto (superusuario)
export const deleteTenantData = async (tenantId) => {
  if (!tenantId) throw new Error('tenantId es requerido');
  try {
    console.log(`🗑️ Eliminando datos del tenant: ${tenantId}`);
    const collections = [
      'appointments',
      'employees',
      'services',
      'inventory',
      'accounting',
      'availability',
      'dateControl',
      'users',
    ];

    let deleted = 0;
    const perCollection = {};

    for (const col of collections) {
      const q = query(collection(db, col), where('tenantId', '==', tenantId));
      const snap = await getDocs(q);
      let count = 0;
      for (const d of snap.docs) {
        await deleteDoc(d.ref);
        count++;
      }
      perCollection[col] = count;
      deleted += count;
    }

    // Eliminar registro del negocio (puede llamarse businesses o tenants)
    const bizSnap = await getDocs(query(collection(db, 'businesses'), where('tenantId', '==', tenantId)));
    let bizDeleted = 0;
    for (const d of bizSnap.docs) { await deleteDoc(d.ref); bizDeleted++; }
    perCollection['businesses'] = bizDeleted;
    deleted += bizDeleted;

    const tenantsSnap = await getDocs(query(collection(db, 'tenants'), where('tenantId', '==', tenantId)));
    let tenantsDeleted = 0;
    for (const d of tenantsSnap.docs) { await deleteDoc(d.ref); tenantsDeleted++; }
    perCollection['tenants'] = tenantsDeleted;
    deleted += tenantsDeleted;

    return { success: true, deleted, perCollection };
  } catch (e) {
    console.error('Error eliminando datos de tenant:', e);
    throw e;
  }
};

// Eliminar negocio completamente (incluyendo custom claims)
export const deleteBusinessCompletely = async (tenantId) => {
  if (!tenantId) throw new Error('tenantId es requerido');
  try {
    console.log(`🗑️ Eliminando negocio completamente: ${tenantId}`);
    // HTTP directo con token y CORS permitido (evita logs del SDK en algunos navegadores)
    const user = auth.currentUser;
    if (!user) throw new Error('Usuario no autenticado');
    const idToken = await user.getIdToken();
    const resp = await fetch('https://europe-west1-gestion-integral-unas.cloudfunctions.net/deleteBusinessCompletelyHttp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ tenantId })
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => '');
      throw new Error(`HTTP ${resp.status}: ${txt || 'error'}`);
    }
    const data = await resp.json();
    return data;
  } catch (error) {
    console.error('Error eliminando negocio completamente:', error);
    throw error;
  }
};






// Función helper para suscribirse a documentos de un tenant específico
export const subscribeToTenantDocuments = (collectionName, tenantId, callback, orderByField = null) => {
  if (!tenantId) {
    console.warn('⚠️ tenantId no proporcionado para suscripción');
    return () => {};
  }
  
  let q = collection(db, collectionName);
  q = query(q, where('tenantId', '==', tenantId));
  
  if (orderByField) {
    q = query(q, orderBy(orderByField, 'desc'));
  }
  
  return onSnapshot(q, (snapshot) => {
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(docs);
  }, (error) => {
    // Manejar específicamente errores de permisos
    if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
      console.log('🔍 Permisos insuficientes para acceder a datos (esto es normal para clientes)');
      callback([]); // Devolver array vacío en caso de error de permisos
    } else {
      console.error('❌ Error en suscripción:', error);
    }
  });
};

// ===== FUNCIÓN CRÍTICA: ELIMINAR TODAS LAS BASES DE DATOS =====
// ⚠️ FUNCIÓN EXTREMADAMENTE PELIGROSA - SOLO PARA SUPERUSUARIOS ⚠️
export const deleteAllDatabases = async (confirmationCode = null) => {
  try {
    // Verificación de seguridad adicional
    if (!confirmationCode || confirmationCode !== 'DELETE_ALL_DATA_CONFIRMED') {
      throw new Error('Código de confirmación requerido para eliminar todos los datos');
    }
    
    // Verificar que el usuario actual es global admin
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado');
    }
    
    const token = await user.getIdTokenResult();
    if (!token.claims.globalAdmin && token.claims.role !== 'globalAdmin') {
      throw new Error('Solo los administradores globales pueden eliminar todos los datos');
    }
    
    console.log('🚨 INICIANDO ELIMINACIÓN COMPLETA DE TODAS LAS BASES DE DATOS...');
    console.log('🔐 Usuario autorizado:', user.email);
    console.log('⏰ Timestamp:', new Date().toISOString());
    
    // Lista ampliada de colecciones a eliminar (incluye residuales y módulos nuevos)
    const collectionsToDelete = [
      // Núcleo de reservas/negocio
      'appointments', 'users', 'employees', 'services', 'inventory',
      'availability', 'dateControl', 'businesses', 'tenants',
      // Contabilidad
      'accounting', 'accounting_stats', 'accounting_categories',
      // Configuración
      'appConfig', 'appConfigTenants',
      // Calendario y planificación de personal
      'availableDates', 'staffSchedules', 'shiftTypes', 'employeeLimits', 'schedules',
      // Historiales
      'userHistory', 'employeeHistory',
      // Soporte
      'supportMessages'
    ];
    
    let totalDeleted = 0;
    const results = {};

    // Helper: borrar colección completa en lotes (permite excluir IDs)
    const deleteEntireCollection = async (collectionName, options = {}) => {
      const { excludeIds = [] } = options;
      const snapshot = await getDocs(collection(db, collectionName));
      if (snapshot.empty) return 0;
      let count = 0;
      const docs = snapshot.docs.filter(d => !excludeIds.includes(d.id));
      for (let i = 0; i < docs.length; i += 400) {
        const slice = docs.slice(i, i + 400);
        const batch = (await import('firebase/firestore')).writeBatch(db);
        slice.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        count += slice.length;
      }
      return count;
    };

    // Eliminar cada colección
    for (const collectionName of collectionsToDelete) {
      try {
        console.log(`🗑️ Eliminando colección: ${collectionName}`);
        const exclude = (collectionName === 'users') ? [user.uid] : [];
        const deletedCount = await deleteEntireCollection(collectionName, { excludeIds: exclude });
        totalDeleted += deletedCount;
        results[collectionName] = deletedCount;
        
        console.log(`✅ Colección ${collectionName}: ${deletedCount} documentos eliminados`);
        
      } catch (error) {
        console.error(`❌ Error eliminando colección ${collectionName}:`, error);
        results[collectionName] = { error: error.message };
      }
    }
    
    // Sembrar estructura mínima (restablecer a valores de fábrica)
    try {
      const configRef = doc(db, 'appConfig', 'main');
      await setDoc(configRef, {
        appName: 'Reserberry',
        logoUrl: '',
        welcomeMessage: '',
        welcomeMessageEnabled: false,
        primaryColor: '#8b5cf6',
        secondaryColor: '#ec4899',
        requireAdminApproval: false,
        updatedAt: serverTimestamp()
      });
      results.factorySeeded = ['appConfig/main'];
      results.superAdminPreserved = user.uid;
      console.log('🌱 Estructura mínima recreada: appConfig/main');
    } catch (seedError) {
      console.error('⚠️ Error sembrando estructura mínima:', seedError);
      results.factorySeedError = seedError?.message || String(seedError);
    }

    console.log(`🎯 ELIMINACIÓN COMPLETADA: ${totalDeleted} documentos eliminados en total`);
    
    return {
      success: true,
      totalDeleted,
      results,
      message: `Se eliminaron ${totalDeleted} documentos de ${collectionsToDelete.length} colecciones`
    };
    
  } catch (error) {
    console.error('💥 ERROR CRÍTICO durante eliminación:', error);
    throw new Error(`Error durante la eliminación: ${error.message}`);
  }
};

// Eliminar todos los usuarios de Firebase Auth (solo para superusuarios)
export const deleteAllAuthUsers = async () => {
  const fn = httpsCallable(functions, 'dangerDeleteAllAuthUsers');
  const res = await fn();
  return res.data;
};

// Migrar negocios existentes para agregar slugs
export const migrateBusinessSlugs = async () => {
  const fn = httpsCallable(functions, 'migrateBusinessSlugs');
  const res = await fn();
  return res.data;
};

// Verificar estado de slugs de negocios
export const checkBusinessSlugs = async () => {
  const fn = httpsCallable(functions, 'checkBusinessSlugs');
  const res = await fn();
  return res.data;
};

// Asignar globalAdmin temporal
export const makeMeGlobalAdmin = async () => {
  const fn = httpsCallable(functions, 'makeMeGlobalAdmin');
  const res = await fn();
  return res.data;
};

// Exponer objetos útiles para depuración desde consola del navegador
try {
  if (typeof window !== 'undefined') {
    window.__appAuth = auth;
  }
} catch (_) {}

// Forzar actualización de slugs para todos los negocios
export const forceUpdateSlugs = async () => {
  const fn = httpsCallable(functions, 'forceUpdateSlugs');
  const res = await fn();
  return res.data;
};

// Obtener detalles completos de un negocio
export const getBusinessDetails = async (tenantId) => {
  try {
    const fn = httpsCallable(functions, 'getBusinessDetails');
    const res = await fn({ tenantId });
    return res.data;
  } catch (error) {
    console.error('Error obteniendo detalles del negocio:', error);
    throw error;
  }
};

// Generar backup de un negocio específico
export const generateBusinessBackup = async (tenantId, options = {}) => {
  try {
    const fn = httpsCallable(functions, 'generateBusinessBackup');
    const res = await fn({ 
      tenantId, 
      includeUsers: options.includeUsers !== false,
      includeAppointments: options.includeAppointments !== false,
      includeServices: options.includeServices !== false,
      includeEmployees: options.includeEmployees !== false
    });
    return res.data;
  } catch (error) {
    console.error('Error generando backup del negocio:', error);
    throw error;
  }
};

// Generar backup completo de todos los negocios
export const generateFullBackup = async () => {
  try {
    const fn = httpsCallable(functions, 'generateFullBackup');
    const res = await fn({});
    return res.data;
  } catch (error) {
    console.error('Error generando backup completo:', error);
    throw error;
  }
};

// Obtener lista de backups
export const getBackups = async () => {
  try {
    const fn = httpsCallable(functions, 'getBackups');
    const res = await fn({});
    return res.data;
  } catch (error) {
    console.error('Error obteniendo backups:', error);
    throw error;
  }
};

// Descargar backup
export const downloadBackup = async (backupId) => {
  try {
    const fn = httpsCallable(functions, 'downloadBackup');
    const res = await fn({ backupId });
    return res.data;
  } catch (error) {
    console.error('Error descargando backup:', error);
    throw error;
  }
};

// Función para descargar archivo JSON
export const downloadJSON = (data, filename) => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Configurar backups programados
export const configureScheduledBackups = async (config) => {
  try {
    const fn = httpsCallable(functions, 'configureScheduledBackups');
    const res = await fn(config);
    return res.data;
  } catch (error) {
    console.error('Error configurando backups programados:', error);
    throw error;
  }
};

// Obtener configuración de backups programados
export const getScheduledBackupConfig = async () => {
  try {
    const configDoc = await getDoc(doc(db, 'appConfig', 'main'));
    if (configDoc.exists()) {
      const config = configDoc.data();
      return config.scheduledBackups || {
        enabled: false,
        scheduleTime: '02:00',
        destinationFolder: 'C\\Backups',
        intervalDays: 1
      };
    }
    return {
      enabled: false,
      scheduleTime: '02:00',
      destinationFolder: 'C\\Backups',
      intervalDays: 1
    };
  } catch (error) {
    console.error('Error obteniendo configuración de backups programados:', error);
    throw error;
  }
};

// Obtener configuración de duración del trial
export const getTrialConfig = async () => {
  try {
    const configDoc = await getDoc(doc(db, 'appConfig', 'main'));
    if (configDoc.exists()) {
      const config = configDoc.data();
      return config.trialConfig || {
        trialDurationDays: 30
      };
    }
    return {
      trialDurationDays: 30
    };
  } catch (error) {
    console.error('Error obteniendo configuración de trial:', error);
    throw error;
  }
};

// Actualizar configuración de duración del trial
export const updateTrialConfig = async (trialDurationDays) => {
  try {
    const fn = httpsCallable(functions, 'updateTrialConfig');
    const res = await fn({ trialDurationDays });
    return res.data;
  } catch (error) {
    console.error('Error actualizando configuración de trial:', error);
    throw error;
  }
};

// Corregir configuración de trial (función temporal)
export const fixTrialConfig = async () => {
  try {
    const fn = httpsCallable(functions, 'fixTrialConfig');
    const res = await fn({});
    return res.data;
  } catch (error) {
    console.error('Error corrigiendo configuración de trial:', error);
    throw error;
  }
};


// ==================== SISTEMA DE ATENCIÓN AL CLIENTE ====================

// Enviar mensaje de soporte
export const sendSupportMessage = async (message, tenantId, businessName) => {
  try {
    const sendSupportMessageFn = httpsCallable(functions, 'sendSupportMessage');
    const result = await sendSupportMessageFn({
      message,
      tenantId,
      businessName
    });
    return result.data;
  } catch (error) {
    console.error('Error enviando mensaje de soporte:', error);
    throw error;
  }
};

// Obtener mensajes de soporte
export const getSupportMessages = async (tenantId = null, limit = 50) => {
  try {
    const getSupportMessagesFn = httpsCallable(functions, 'getSupportMessages');
    const result = await getSupportMessagesFn({
      tenantId,
      limit
    });
    return result.data;
  } catch (error) {
    console.error('Error obteniendo mensajes de soporte:', error);
    throw error;
  }
};

// Responder a un mensaje de soporte
export const replyToSupportMessage = async (messageId, reply, tenantId = null) => {
  try {
    const replyToSupportMessageFn = httpsCallable(functions, 'replyToSupportMessage');
    const result = await replyToSupportMessageFn({
      messageId,
      reply,
      tenantId
    });
    return result.data;
  } catch (error) {
    console.error('Error respondiendo mensaje de soporte:', error);
    throw error;
  }
};

// Marcar mensaje como leído
export const markSupportMessageAsRead = async (messageId) => {
  try {
    const markSupportMessageAsReadFn = httpsCallable(functions, 'markSupportMessageAsRead');
    const result = await markSupportMessageAsReadFn({
      messageId
    });
    return result.data;
  } catch (error) {
    console.error('Error marcando mensaje como leído:', error);
    throw error;
  }
};

// Suscribirse a mensajes de soporte en tiempo real
export const subscribeToSupportMessages = (tenantId, callback, errorCallback = null) => {
  try {
    if (!tenantId) {
      // Si no hay tenantId no nos suscribimos para evitar errores de permisos
      return () => {};
    }
    const q = query(
      collection(db, 'supportMessages'),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      callback(messages);
    }, (error) => {
      if (errorCallback) {
        errorCallback(error);
      } else {
        console.error('Error suscribiéndose a mensajes de soporte:', error);
      }
    });
  } catch (error) {
    console.error('Error suscribiéndose a mensajes de soporte:', error);
    if (errorCallback) {
      errorCallback(error);
    } else {
      throw error;
    }
  }
};

// Suscribirse a todos los mensajes (para superadministrador)
export const subscribeToAllSupportMessages = (callback, errorCallback = null) => {
  try {
    const q = query(
      collection(db, 'supportMessages'),
      orderBy('createdAt', 'asc')
    );
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
      }));
      callback(messages);
    }, (error) => {
      if (errorCallback) errorCallback(error); else console.error('Error suscribiéndose a todos los mensajes de soporte:', error);
    });
  } catch (error) {
    console.error('Error suscribiéndose a todos los mensajes de soporte:', error);
    if (errorCallback) errorCallback(error); else throw error;
  }
};

// Limpiar todos los mensajes de soporte
export const clearAllSupportMessages = async () => {
  try {
    const clearAllSupportMessagesFn = httpsCallable(functions, 'clearAllSupportMessages');
    const result = await clearAllSupportMessagesFn();
    return result.data;
  } catch (error) {
    console.error('Error limpiando mensajes de soporte:', error);
    throw error;
  }
};

export const markSupportMessagesAsRead = async (tenantId) => {
  try {
    const markSupportMessagesAsReadFn = httpsCallable(functions, 'markSupportMessagesAsRead');
    const result = await markSupportMessagesAsReadFn({ tenantId });
    return result.data;
  } catch (error) {
    console.error('Error marcando mensajes como vistos:', error);
    throw error;
  }
};

export const markSupportMessagesAsReadByGlobalAdmin = async (tenantId) => {
  try {
    const markSupportMessagesAsReadByGlobalAdminFn = httpsCallable(functions, 'markSupportMessagesAsReadByGlobalAdmin');
    const result = await markSupportMessagesAsReadByGlobalAdminFn({ tenantId });
    return result.data;
  } catch (error) {
    console.error('Error marcando mensajes como vistos por superadministrador:', error);
    throw error;
  }
};

// Obtener mensajes para el panel del negocio con conteo de no leídos
export const getSupportMessagesForBusiness = async (tenantId) => {
  try {
    const tid = tenantId || getCachedTenantId();
    if (!tid) return { threads: [], unread: 0 };
    // Leer directamente de Firestore y ordenar en cliente para evitar índices
    const snap = await getDocs(query(collection(db, 'supportMessages'), where('tenantId', '==', tid)));
    const threads = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    threads.sort((a, b) => {
      const ta = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : 0;
      const tb = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : 0;
      return tb - ta;
    });
    // Calcular no leídos para negocio
    const unread = threads.reduce((acc, t) => {
      const st = String((t.status || '')).toLowerCase();
      return acc + (st !== 'read' ? 1 : 0);
    }, 0);
    return { threads, unread };
  } catch (error) {
    console.error('Error obteniendo mensajes para negocio:', error);
    throw error;
  }
};

// Obtener mensajes para el panel del superadministrador con conteo de no leídos
export const getSupportMessagesForGlobalAdmin = async () => {
  try {
    const getSupportMessagesForGlobalAdminFn = httpsCallable(functions, 'getSupportMessagesForGlobalAdmin');
    const result = await getSupportMessagesForGlobalAdminFn({});
    return result.data;
  } catch (error) {
    console.error('Error obteniendo mensajes para superadministrador:', error);
    throw error;
  }
};

// Funciones de recordatorios
export const getReminderSettings = async (tenantId) => {
  try {
    const settingsRef = doc(db, 'reminderSettings', tenantId);
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      return settingsSnap.data();
    } else {
      // Configuración por defecto
      return {
        enabled: false,
        reminderWindows: [24, 2], // 24 horas y 2 horas antes
        emailTemplate: {
          subject: 'Recordatorio de cita',
          body: 'Hola {{clientName}}, te recordamos que tienes una cita el {{date}} a las {{time}} en {{businessName}}.'
        },
        businessHours: {
          start: '09:00',
          end: '20:00'
        }
      };
    }
  } catch (error) {
    console.error('Error obteniendo configuración de recordatorios:', error);
    throw error;
  }
};

export const saveReminderSettings = async (tenantId, settings) => {
  try {
    const settingsRef = doc(db, 'reminderSettings', tenantId);
    // Compatibilidad: derivar formato antiguo (reminderWindows + emailTemplate)
    let legacyReminderWindows = undefined;
    let legacyEmailTemplate = undefined;
    try {
      if (settings?.windows) {
        const entries = Object.entries(settings.windows)
          .filter(([_, w]) => w && typeof w.hours === 'number' && w.enabled !== false)
          .map(([_, w]) => w.hours)
          .sort((a, b) => b - a);
        if (entries.length) legacyReminderWindows = entries;
      }
      if (settings?.templates) {
        const base = settings.templates['24h'] || settings.templates['2h'];
        if (base && (base.subject || base.content)) {
          legacyEmailTemplate = {
            subject: base.subject || 'Recordatorio de cita',
            body: (base.content || '').replaceAll('{nombre_cliente}', '{{clientName}}')
                                      .replaceAll('{fecha}', '{{date}}')
                                      .replaceAll('{hora}', '{{time}}')
                                      .replaceAll('{servicio}', '{{service}}')
                                      .replaceAll('{negocio}', '{{businessName}}')
          };
        }
      }
    } catch {}

    await setDoc(settingsRef, {
      ...settings,
      ...(legacyReminderWindows ? { reminderWindows: legacyReminderWindows } : {}),
      ...(legacyEmailTemplate ? { emailTemplate: legacyEmailTemplate } : {}),
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error guardando configuración de recordatorios:', error);
    throw error;
  }
};

export const sendTestReminderEmail = async (tenantId, testEmail) => {
  try {
    const sendTestReminderEmailFn = httpsCallable(functions, 'sendTestReminderEmail');
    const result = await sendTestReminderEmailFn({ tenantId, testEmail });
    return result.data;
  } catch (error) {
    console.error('Error enviando email de prueba:', error);
    throw error;
  }
};

// Función para actualizar campos de trial en negocios existentes
export const updateBusinessWithTrialFields = async (tenantId) => {
  try {
    const updateBusinessWithTrialFieldsFn = httpsCallable(functions, 'updateBusinessWithTrialFields');
    const result = await updateBusinessWithTrialFieldsFn({ tenantId });
    return result.data;
  } catch (error) {
    console.error('Error actualizando campos de trial:', error);
    throw error;
  }
};

// Función para actualizar negocio a plan Pro
export const upgradeToPro = async (tenantId) => {
  try {
    const upgradeToProFn = httpsCallable(functions, 'upgradeToPro');
    const result = await upgradeToProFn({ tenantId });
    return result.data;
  } catch (error) {
    console.error('Error actualizando a plan Pro:', error);
    throw error;
  }
};

// Función para crear sesión de checkout de Stripe (placeholder)
export const createCheckoutSession = async (tenantId, planId) => {
  try {
    const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckoutSessionFn({ tenantId, planId });
    return result.data;
  } catch (error) {
    console.error('Error creando sesión de checkout:', error);
    throw error;
  }
};

// Función para obtener datos del negocio
export const getBusinessData = async (tenantId) => {
  try {
    const businessRef = doc(db, 'businesses', tenantId);
    const businessSnap = await getDoc(businessRef);
    
    if (businessSnap.exists()) {
      return businessSnap.data();
    } else {
      throw new Error('Negocio no encontrado');
    }
  } catch (error) {
    console.error('Error obteniendo datos del negocio:', error);
    throw error;
  }
};

// Función para restablecer contraseña de usuario (solo superadmin)
export const resetUserPassword = async (email) => {
  try {
    const resetUserPasswordFn = httpsCallable(functions, 'resetUserPassword');
    const result = await resetUserPasswordFn({ email });
    return result.data;
  } catch (error) {
    console.error('Error restableciendo contraseña:', error);
    throw error;
  }
};

export const deleteBackup = async (backupId) => {
  try {
    const deleteBackupFn = httpsCallable(functions, 'deleteBackup');
    const result = await deleteBackupFn({ backupId });
    return result.data;
  } catch (error) {
    console.error('Error eliminando backup:', error);
    throw error;
  }
};

export const processStripePayment = async (tenantId, stripeCustomerId, subscriptionId) => {
  try {
    const processPayment = httpsCallable(functions, 'processStripePayment');
    const result = await processPayment({ 
      tenantId, 
      stripeCustomerId, 
      subscriptionId 
    });
    return result.data;
  } catch (error) {
    console.error('Error procesando pago de Stripe:', error);
    throw error;
  }
};

export const updateExistingProBusinesses = async () => {
  try {
    const updateBusinesses = httpsCallable(functions, 'updateExistingProBusinesses');
    const result = await updateBusinesses();
    return result.data;
  } catch (error) {
    console.error('Error actualizando negocios Pro:', error);
    throw error;
  }
};

export const restoreBackupFromFile = async (backupData, confirmRestore = true) => {
  try {
    const fn = httpsCallable(functions, 'restoreBackupFromFile');
    const res = await fn({ backupData, confirmRestore });
    return res.data;
  } catch (error) {
    console.error('Error restaurando backup desde archivo:', error);
    throw error;
  }
};

// ===== CSV Import (usuarios)
export const importUsersCsvPreview = async (tenantId, rows) => {
  try {
    const fn = httpsCallable(functions, 'importCsvPreview');
    const res = await fn({ tenantId, type: 'users', rows });
    return res.data;
  } catch (error) {
    console.error('Error en importUsersCsvPreview:', error);
    throw error;
  }
};

export const importUsersCsvExecute = async (tenantId, rows) => {
  try {
    const fn = httpsCallable(functions, 'importCsvExecute');
    const res = await fn({ tenantId, type: 'users', rows });
    return res.data;
  } catch (error) {
    console.error('Error en importUsersCsvExecute:', error);
    throw error;
  }
};

// ===== Purga de citas antiguas por tenant
export const purgeOldAppointments = async (tenantId, olderThanDays = 180) => {
  try {
    const fn = httpsCallable(functions, 'purgeOldAppointments');
    const res = await fn({ tenantId, olderThanDays });
    return res.data;
  } catch (error) {
    console.error('Error purgando citas antiguas:', error);
    throw error;
  }
};

export default app;
